import { COUNTRY_OPTIONS, ONBOARDING_DATA_TYPE_OPTIONS } from '@deepguide-ai/types'
import { PickerDialog } from '@/pages/risk/RiskDetailSections'
import { VendorCustomerDataSelect } from './VendorCustomerDataSelect'
import { MultiSelect } from '@deepguide-ai/ui/multi-select'
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ComponentProps,
  createContext,
  useContext,
} from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from '@tanstack/react-router'
import {
  Plus,
  Check,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Sparkles,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useSession } from '@/lib/auth-client'
import { trpc } from '@/lib/trpc'
import { useHipaaEnabled } from '@/hooks/useComplianceFramework'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@deepguide-ai/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { DatePicker } from '@deepguide-ai/ui/date-picker'
import { SimpleInstructionsEditor } from '@/components/editors/SimpleInstructionsEditor'
import { UserSelect, type UserSelectMember } from '@/components/ui/user-select'
import { VendorRiskResponseFields, VendorRiskResponseReadOnly } from './VendorRiskResponseFields'
import { VendorAssessmentInputsPanel } from './VendorAssessmentInputsPanel'
import {
  getEvidenceCompleteness,
  type EvidenceRequest,
} from './vendorAssessmentDocumentRows'
import type { FieldProvenanceMap, VendorAssessmentDTO, VendorRiskResponse } from '@deepguide-ai/types'
import { VendorAssessmentContextSchema, RISK_LEVELS, RISK_LEVEL_LABELS, computeRiskScore, deriveRating } from '@deepguide-ai/types'
import { VENDOR_AUTH_METHOD_LABELS, VENDOR_ENVIRONMENT_ACCESS_LABELS, VENDOR_SUBPROCESSOR_OPTIONS } from '@/pages/vendors/type'
import { urlHost } from '@deepguide-ai/shared'
import { DOC_TYPE_LABELS } from '@/pages/vendors/doc-type-labels'
import { CLASSIFICATION_LABELS } from '@/pages/assets/type'
import RiskMatrixCard from '@/pages/risk/RiskMatrixCard'
import { riskRatingSignal } from '@/lib/status-colors'
import {
  DETAIL_FIELD_LABEL_CLASS,
  DETAIL_FIELD_VALUE_CLASS,
  DetailPanelTitle,
  DetailStatusGrid,
  DetailStatusItem,
} from '@/components/compliance/detail-primitives'
import {
  makeFieldVisibility,
  isHighRiskTier,
  hasFieldValue,
} from './vendorAssessmentFieldTiers'

const VENDOR_AI_ASSESSMENT_FLAG = 'vendorAiAssessment'

// ── Types ──

type AssessmentStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'NEEDS_REVISION'



export function VendorUsageFields({ context, editable, onChange }: {
  context: Record<string, unknown>
  editable: boolean
  onChange: (patch: Record<string, unknown>) => void
}) {
  return <section aria-label='Your use of this vendor' className='space-y-4'>
    <SectionHeading>Your use of this vendor</SectionHeading>
    <p className='text-text-subtle text-xs'>Describe how your company uses this vendor. Vera uses these answers alongside the vendor documents.</p>
    <div className='grid gap-4 sm:grid-cols-2'>
      {([
        ['businessCriticality', 'If this vendor became unavailable, how would it affect your operations?', { HIGH: 'High: core operations would stop', MEDIUM: 'Medium: disruption, but a workaround exists', LOW: 'Low: limited disruption' }],
        ['vendorToCompanyAuthMethod', 'Vendor access to your company', VENDOR_AUTH_METHOD_LABELS],
        ['companyToVendorAuthMethod', 'Your company access to vendor', VENDOR_AUTH_METHOD_LABELS],
        ['environmentAccess', 'Vendor access to your environment', VENDOR_ENVIRONMENT_ACCESS_LABELS],
      ] as [string, string, Record<string, string>][]).map(([key, label, options]) => (
        <div key={key} className='space-y-1'>
          <Label className={cn(DETAIL_FIELD_LABEL_CLASS, 'mb-1.5')} htmlFor={editable ? `vendor-usage-${key}` : undefined}>{label}</Label>
          {editable ? <Select value={context[key] != null && String(context[key]) in options ? String(context[key]) : '_unknown'}
            onValueChange={value => onChange({ [key]: value === '_unknown' ? null : value })}>
            <SelectTrigger id={`vendor-usage-${key}`} fullWidth><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value='_unknown'>Unknown</SelectItem>
              {Object.entries(options).map(([value, text]) => <SelectItem key={value} value={value}>{text}</SelectItem>)}
            </SelectContent>
          </Select> : <ReadonlyText value={context[key] == null ? null : options[String(context[key])]} />}
        </div>
      ))}
    </div>
  </section>
}

export interface VendorAssessmentDialogProps {
  assessmentId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdate: () => void
  /**
   * 'dialog' (default) renders inside a modal; 'page' renders the same editor
   * as a full-page surface; 'inline' embeds it in the vendor pipeline.
   */
  mode?: 'dialog' | 'page' | 'inline'
  readOnly?: boolean
  headerFields?: React.ReactNode
  intake?: React.ReactNode
  documents?: React.ReactNode
  initialStage?: 'open' | 'documents' | 'assessment'
}

/**
 * Chrome wrapper: a modal in 'dialog' mode, a full-page container in 'page'
 * mode. The editor body + footer are passed as children unchanged, so both
 * surfaces share one implementation.
 */
function AssessmentShell({
  mode,
  open,
  onOpenChange,
  title,
  code,
  statusBadge,
  vendorName,
  facts,
  banners,
  footer,
  children,
}: {
  mode: 'dialog' | 'page' | 'inline'
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  code: string | null
  statusBadge: React.ReactNode
  vendorName: string | null
  facts: React.ReactNode
  banners: React.ReactNode
  footer: React.ReactNode
  children: React.ReactNode
}) {
  // As a page this is a detail record like any other, so it uses the same
  // chrome as the vendor/asset/risk detail pages: full-bleed white, a title
  // block with the record code, and a status grid — not a floating card.
  if (mode === 'inline') {
    return <section aria-label='Vendor assessment pipeline'>{facts}{banners}{children}{footer}</section>
  }

  if (mode === 'page') {
    return (
      <div className='flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-card'>
          <ScrollArea
            className='h-full min-h-0 flex-1'
            viewClassName='[&>div]:!block px-8 pt-8'
          >
            <div className='mx-auto w-full max-w-5xl pb-8'>
              <DetailPanelTitle
                code={code ?? undefined}
                title={title}
              />
              {facts}
              {banners}
              <div className='mt-8'>{children}</div>
              {footer}
            </div>
          </ScrollArea>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='flex h-[85vh] max-w-2xl flex-col overflow-hidden p-0'
        closeClassName='top-4.5 right-4'
      >
        <DialogHeader className='border-border shrink-0 border-b px-6 py-4'>
          <div className='flex items-center justify-between pr-5'>
            <div className='flex items-center gap-3'>
              <DialogTitle className='font-heading text-foreground text-[15px] font-semibold'>
                {title}
              </DialogTitle>
              {statusBadge}
            </div>
            {vendorName && (
              <span className='font-body text-text-placeholder text-[11px]'>
                {vendorName}
              </span>
            )}
          </div>
          {banners}
        </DialogHeader>
        <ScrollArea
          className='min-h-0 flex-1'
          viewClassName='[&>div]:!block [&>div]:px-6'
        >
          {children}
        </ScrollArea>
        <div className='border-border shrink-0 border-t px-6'>{footer}</div>
      </DialogContent>
    </Dialog>
  )
}

// ── Section heading (single-page form) ──

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className='font-heading text-text-subtle text-[11px] font-semibold tracking-wide uppercase'>
      {children}
    </h3>
  )
}

// ── Assessment Status Badge ──

export function AssessmentStatusBadge({
  status,
}: {
  status: AssessmentStatus | string
}) {
  const statuses: Record<string, ComponentProps<typeof Badge>['status']> = {
    DRAFT: 'default',
    SUBMITTED: 'amber',
    APPROVED: 'green',
    REJECTED: 'red',
    NEEDS_REVISION: 'blue',
  }
  const labels: Record<string, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    NEEDS_REVISION: 'Needs Revision',
  }
  return (
    <Badge
      status={statuses[status] ?? 'default'}
      className='font-body h-5 text-[10px]'
    >
      {labels[status] ?? status}
    </Badge>
  )
}

// ── Bool select helper ──

function BoolSelect({
  value,
  onChange,
  trueLabel = 'Yes',
  falseLabel = 'No',
  label,
  unknownLabel = 'Not set',
}: {
  value: boolean | null | undefined
  onChange: (val: boolean | null) => void
  trueLabel?: string
  falseLabel?: string
  label?: string
  unknownLabel?: string
}) {
  const strVal = value === true ? 'yes' : value === false ? 'no' : '_unset'
  return (
    <Select
      value={strVal}
      onValueChange={(v) =>
        onChange(v === 'yes' ? true : v === 'no' ? false : null)
      }
    >
      <SelectTrigger aria-label={label} className='border-border-divider h-8 w-full rounded-sm text-[11px]'>
        <SelectValue placeholder={unknownLabel} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value='_unset'>{unknownLabel}</SelectItem>
        <SelectItem value='yes'>{trueLabel}</SelectItem>
        <SelectItem value='no'>{falseLabel}</SelectItem>
      </SelectContent>
    </Select>
  )
}

// ── Field label ──

/**
 * RFC-231 §3.2 — per-field provenance. Tabs read the map through context so
 * a label only needs its dotted `path` to render the AI chip + Accept/Discard.
 */
interface ProvenanceContextValue {
  provenance: FieldProvenanceMap
  editable: boolean
  busyPath: string | null
  documentLabels: Record<string, string>
  documentLinks?: Record<string, string>
  onAccept: (path: string) => void
  onDiscard: (path: string) => void
}
/** The "AI drafted" chip — shared by the field chip and the section badge. */
const AI_CHIP_CLASS =
  'font-body inline-flex h-4 items-center gap-1 rounded-sm bg-cobalt-tonal px-1.5 text-[9px] font-semibold text-cobalt-strong'
const ProvenanceContext = createContext<ProvenanceContextValue>({
  provenance: {},
  editable: false,
  busyPath: null,
  documentLabels: {},
  onAccept: () => {},
  onDiscard: () => {},
})

function ProvenanceChip({ path }: { path: string }) {
  const ctx = useContext(ProvenanceContext)
  const entry = ctx.provenance[path]
  if (!entry) return null
  const source =
    entry.source === 'analysis' ? 'Vera assessment' : entry.source === 'research'
      ? `web${entry.url ? `: ${urlHost(entry.url)}` : ''}`
      : `${ctx.documentLabels[entry.vaultDocumentId ?? ''] ?? 'document'}${entry.page ? ` p.${entry.page}` : ''}`
  const busy = ctx.busyPath === path
  if (!ctx.editable) return (
    <details className='mt-1 text-xs font-normal text-muted-foreground'>
      <summary className='cursor-pointer'>{entry.accepted ? 'Accepted source' : 'AI-drafted source'}</summary>
      <div className='mt-2 space-y-1 break-words'>
        <p>{source}</p>
        {entry.quote && <blockquote className='border-l pl-3'>{entry.quote}</blockquote>}
        {entry.url && <p>{entry.url}</p>}
        {entry.vaultDocumentId && ctx.documentLinks?.[entry.vaultDocumentId] && <a className='underline' href={ctx.documentLinks[entry.vaultDocumentId]}>View {ctx.documentLabels[entry.vaultDocumentId]}</a>}
        <p>Extracted: {entry.extractedAt}</p>
        {entry.confidence != null && <p>Confidence: {entry.confidence}</p>}
      </div>
    </details>
  )
  return (
    <span className='ml-1.5 inline-flex items-center gap-1 align-middle'>
      <span
        title={entry.source === 'analysis' ? 'Vera proposed this rating from the assessment context and available evidence. Review it before approval.' : entry.quote ? `“${entry.quote}”` : 'The model wrote this value without quoting the document. Verify it against the source.'}
        className={entry.accepted ? `${AI_CHIP_CLASS} bg-muted text-muted-foreground` : AI_CHIP_CLASS}
      >
        {!entry.accepted && <Sparkles className='h-2.5 w-2.5' />}
        {entry.accepted ? 'Accepted' : `AI · ${source}${entry.quote ? '' : ' · no quote'}`}
      </span>
      {ctx.editable && !entry.accepted && (
        /* Bordered mini-buttons, not links — these repeat on every AI-filled
           label, so they stay chip-height but must read as actions. */
        <>
          <button
            type='button'
            disabled={busy}
            onClick={() => ctx.onAccept(path)}
            title='Keep this AI-written value'
            className='font-body border-border-divider text-text-body hover:border-status-green/40 hover:bg-status-green-bg hover:text-status-green inline-flex h-[18px] items-center gap-0.5 rounded-sm border bg-card px-1 text-[9.5px] font-medium transition-colors disabled:opacity-50'
          >
            <Check className='h-2.5 w-2.5' />
            Accept
          </button>
          <button
            type='button'
            disabled={busy}
            onClick={() => ctx.onDiscard(path)}
            title='Clear the value and its provenance'
            className='font-body border-border-divider text-text-body hover:border-status-red/40 hover:bg-status-red-bg hover:text-status-red inline-flex h-[18px] items-center gap-0.5 rounded-sm border bg-card px-1 text-[9.5px] font-medium transition-colors disabled:opacity-50'
          >
            <X className='h-2.5 w-2.5' />
            Discard
          </button>
        </>
      )}
    </span>
  )
}

function FieldLabel({
  children,
  required,
  path,
  htmlFor,
}: {
  children: React.ReactNode
  required?: boolean
  /** Dotted provenance path, e.g. `securityPosture.mfaEnforced` (RFC-231). */
  path?: string
  htmlFor?: string
}) {
  return (
    <Label htmlFor={htmlFor} className={cn(DETAIL_FIELD_LABEL_CLASS, 'mb-1.5')}>
      {children}
      {/* Decorative — the control carries `aria-required`, so announcing the
          asterisk too would just read the label as "… star". */}
      {required && (
        <span aria-hidden='true' className='text-destructive ml-0.5'>
          *
        </span>
      )}
      {path && <ProvenanceChip path={path} />}
    </Label>
  )
}

/** SOC 2 sub-field label (inside the nested `soc2` object). */
function SubLabel({ children, path }: { children: React.ReactNode; path: string }) {
  return (
    <span className={cn(DETAIL_FIELD_LABEL_CLASS, 'mb-1.5')}>
      {children}
      <ProvenanceChip path={path} />
    </span>
  )
}

/**
 * RFC-231 §3.1 — questionnaire sections open when they hold values (or right
 * after an assessment run) and stay collapsed while empty, so the documents
 * panel and Assess read as the primary path.
 */
/**
 * Stage rail — linear *guidance* over the review pipeline without gating:
 * every stage can be opened without completing the previous one.
 * "You are here" = the first stage that still needs work.
 */
type ReviewStage = {
  key: string
  label: string
  done?: boolean
}

function AssessmentStageRail({ stages, currentStage, selectedStage, onSelect }: {
  stages: ReviewStage[]
  currentStage: string
  selectedStage: string
  onSelect: (key: string) => void
}) {
  const progressIndex = Math.max(stages.findIndex(stage => stage.key === currentStage), stages.findIndex(stage => stage.key === selectedStage))
  return (
    <nav aria-label='Review stages' className='pb-4'>
      <ol className='flex w-full items-start'>
        {stages.map((stage, index) => {
          const done = index < progressIndex || (index === stages.length - 1 && stage.done)
          return (
          <li key={stage.key} aria-current={currentStage === stage.key ? 'step' : undefined}
            className='relative min-w-0 flex-1 last:flex-none'>
            {index < stages.length - 1 && <span aria-hidden='true'
              className={`absolute top-3.5 left-8 w-[calc(100%_-_2.25rem)] border-t ${done ? 'border-status-green' : 'border-border'}`} />}
            <Button variant='ghost' className='relative h-auto flex-col items-start gap-2 p-0 hover:bg-transparent'
              aria-label={`View ${stage.label}`} aria-pressed={selectedStage === stage.key}
              onClick={() => onSelect(stage.key)}>
              <span aria-hidden='true' className={`relative flex size-7 items-center justify-center rounded-full border ring-4 ring-card ${
                done ? 'border-status-green bg-status-green-bg text-status-green-strong' : selectedStage === stage.key
                  ? 'border-ring bg-selected text-cobalt-strong' : 'border-dashed border-text-placeholder bg-card'
              }`}>
                {done ? <Check className='size-3.5' /> : selectedStage === stage.key ? <span className='size-2.5 rounded-full bg-ring' /> : null}
              </span>
              <span className={`text-[12px] ${done ? 'text-status-green-strong' : selectedStage === stage.key ? 'text-cobalt-strong' : 'text-text-subtle'}`}>{stage.label}</span>
            </Button>
          </li>
          )
        })}
      </ol>
    </nav>
  )
}

function CollapsibleSection({
  title,
  hasValues,
  draftedCount,
  forceOpen,
  editable,
  children,
}: {
  title: string
  hasValues: boolean
  draftedCount: number
  forceOpen: boolean
  editable: boolean
  children: React.ReactNode
}) {
  const [manual, setManual] = useState<boolean | null>(null)
  // A fresh Assess run reopens sections the user had collapsed.
  useEffect(() => {
    if (forceOpen) setManual(null)
  }, [forceOpen])
  const open = manual ?? (hasValues || forceOpen)
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <Collapsible open={open} onOpenChange={setManual}>
      <CollapsibleTrigger asChild>
        <Button variant='ghost' size='sm' className='w-full justify-start px-0 has-[>svg]:px-0'>
          <span className='font-heading text-text-subtle text-[11px] font-semibold tracking-wide uppercase'>
            {title}
          </span>
          {draftedCount > 0 && (
            <span className={AI_CHIP_CLASS}>
              <Sparkles className='h-2.5 w-2.5' />
              {draftedCount} drafted
            </span>
          )}
          {!open && !hasValues && (
            <span className='font-body text-text-placeholder text-xs font-normal'>
              {editable ? 'empty, expand to fill in by hand' : 'Not recorded'}
            </span>
          )}
          <Chevron className='text-text-placeholder ml-auto h-3.5 w-3.5 shrink-0' />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className='pt-4'>{children}</CollapsibleContent>
    </Collapsible>
  )
}

function sectionHasValues(data: Record<string, unknown>): boolean {
  return Object.entries(data).some(([key, value]) => ['vendorContext', 'assessedRisk', 'riskResponse'].includes(key) ? false : hasFieldValue(value))
}

function countDrafted(provenance: FieldProvenanceMap, section: string): number {
  return Object.entries(provenance).filter(
    ([path, entry]) => path.startsWith(`${section}.`) && !entry.accepted
  ).length
}


function HipaaBusinessAssociateFields({ context, editable = false, onChange }: {
  context: Record<string, unknown>
  editable?: boolean
  onChange?: (context: Record<string, unknown>) => void
}) {
  const statuses = { NOT_REQUIRED: 'Not required', REQUIRED: 'Required, not yet signed', SIGNED: 'Signed', EXPIRED: 'Expired' }
  return <section aria-label='HIPAA business associate' className='space-y-4'>
    <SectionHeading>HIPAA Business Associate</SectionHeading>
    <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${editable ? '' : 'lg:grid-cols-4'}`}>
    <div>
      <FieldLabel path='securityPosture.vendorContext.containsEphi'>Processes ePHI</FieldLabel>
      {editable ? <Select value={context.containsEphi === true ? 'true' : context.containsEphi === false ? 'false' : '_unknown'} onValueChange={value => onChange?.({ ...context, containsEphi: value === 'true', ...(value === 'false' ? { baaStatus: 'NOT_REQUIRED' } : context.baaStatus === 'NOT_REQUIRED' ? { baaStatus: 'REQUIRED' } : {}) })}>
        <SelectTrigger aria-label='Processes ePHI' fullWidth><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value='_unknown' disabled>Not recorded</SelectItem><SelectItem value='true'>Yes</SelectItem><SelectItem value='false'>No</SelectItem></SelectContent>
      </Select> : <ReadonlyBool value={context.containsEphi as boolean | undefined} />}
    </div>
    <div>
      <FieldLabel path='securityPosture.vendorContext.baaStatus'>BAA status</FieldLabel>
      {editable ? <Select value={context.baaStatus as string ?? '_unknown'} onValueChange={value => onChange?.({ ...context, baaStatus: value })}>
        <SelectTrigger aria-label='BAA status' fullWidth><SelectValue placeholder='Not recorded' /></SelectTrigger>
        <SelectContent><SelectItem value='_unknown' disabled>Not recorded</SelectItem>{Object.entries(statuses).map(([value, label]) => <SelectItem key={value} value={value} disabled={context.containsEphi === false ? value !== 'NOT_REQUIRED' : context.containsEphi === true && value === 'NOT_REQUIRED'}>{label}</SelectItem>)}</SelectContent>
      </Select> : <ReadonlyText value={statuses[context.baaStatus as keyof typeof statuses] ?? null} />}
    </div>
    {!editable && <>
      {([['baaSignedAt', 'Signed'], ['baaExpiresAt', 'Expires']] as const).map(([key, label]) => <div key={key}>
        <FieldLabel path={`securityPosture.vendorContext.${key}`}>{label}</FieldLabel>
        <ReadonlyText value={context[key] ? new Date(context[key] as string).toLocaleDateString() : null} />
      </div>)}
    </>}
    </div>
    <p className='text-text-subtle text-xs'>{editable ? 'Upload the signed agreement in Documents. These answers take effect after assessment approval.' : 'Signed agreements are available in Documents.'}</p>
  </section>
}

/** Read-only composition; no customer queries or mutation hooks are mounted. */
export function VendorAssessmentReadOnly({ assessment, vendorTier, documentLabels, documentLinks, hipaaEnabled = false }: {
  assessment: Pick<VendorAssessmentDTO, 'securityPosture' | 'dataHandling' | 'complianceCerts' | 'contractualReview' | 'fieldProvenance'>
  vendorTier: string
  documentLabels: Record<string, string>
  documentLinks: Record<string, string>
  hipaaEnabled?: boolean
}) {
  const sections = [
    { title: 'Security', Component: SecurityTab, data: assessment.securityPosture },
    { title: 'Data handling', Component: DataHandlingTab, data: assessment.dataHandling },
    { title: 'Compliance', Component: ComplianceTab, data: assessment.complianceCerts },
    { title: 'Contractual', Component: ContractualTab, data: assessment.contractualReview },
  ]
  return (
    <ProvenanceContext.Provider value={{ provenance: assessment.fieldProvenance ?? {}, editable: false, busyPath: null, documentLabels, documentLinks, onAccept: () => {}, onDiscard: () => {} }}>
      <div className='space-y-6'>
        <VendorRiskResponseReadOnly value={assessment.securityPosture?.riskResponse} />
        {hipaaEnabled && <HipaaBusinessAssociateFields context={assessment.securityPosture?.vendorContext ?? {}} />}
        <div className='space-y-2'>
        {sections.map(({ title, Component, data }) => (
          <CollapsibleSection
            key={title}
            title={title}
            hasValues={data != null && sectionHasValues(data)}
            draftedCount={0}
            forceOpen={false}
            editable={false}
          >
            {data ? <Component data={data} editable={false} onChange={() => {}} vendorTier={vendorTier} hipaaEnabled={hipaaEnabled} {...(Component === DataHandlingTab ? { customerDataInvolved: assessment.securityPosture?.vendorContext?.customerDataInvolved } : {})} /> : <p className='text-sm text-muted-foreground'>Not recorded</p>}
          </CollapsibleSection>
        ))}
        </div>
      </div>
    </ProvenanceContext.Provider>
  )
}

// ── Main Dialog ──

export function VendorAssessmentDialog({
  assessmentId,
  open,
  onOpenChange,
  onUpdate,
  mode = 'dialog',
  readOnly = false,
  headerFields,
  intake,
  documents,
  initialStage,
}: VendorAssessmentDialogProps) {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string
  }
  const session = useSession()
  const currentUserId = session?.data?.user?.id
  const queryClient = useQueryClient()
  const [assessmentActionContainer, setAssessmentActionContainer] = useState<HTMLSpanElement | null>(null)
  const vendorAiAssessmentFlag = useFeatureFlag(VENDOR_AI_ASSESSMENT_FLAG)

  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewAction, setReviewAction] = useState<
    'APPROVED' | 'REJECTED' | 'NEEDS_REVISION'
  >('APPROVED')
  const [reviewComments, setReviewComments] = useState('')

  // Local draft state for all sections
  const [securityDraft, setSecurityDraft] = useState<Record<string, unknown>>(
    {}
  )
  const [dataDraft, setDataDraft] = useState<Record<string, unknown>>({})
  const [complianceDraft, setComplianceDraft] = useState<
    Record<string, unknown>
  >({})
  const [contractualDraft, setContractualDraft] = useState<
    Record<string, unknown>
  >({})
  const [summaryDraft, setSummaryDraft] = useState('')
  const [riskTierDraft, setRiskTierDraft] = useState<string>('')
  const [initializedFor, setInitializedFor] = useState<string | null>(null)
  const [stageSelection, setStageSelection] = useState<{ assessmentId: string; key: string } | null>(null)
  // RFC-231 §3.2 — AI provenance per field; §3.1 — sections open after Assess.
  const [provenance, setProvenance] = useState<FieldProvenanceMap>({})
  const [forceOpenSections, setForceOpenSections] = useState(false)
  const [busyPath, setBusyPath] = useState<string | null>(null)

  // Query
  const assessmentQuery = useQuery({
    ...trpc.vendorAssessment.getById.queryOptions({ id: assessmentId! }),
    enabled: !!assessmentId,
  })
  const eligibleReviewersQuery = useQuery({
    ...trpc.vendorAssessment.listEligibleReviewers.queryOptions(),
    enabled: open,
  })

  // Mutations
  const saveDraftMutation = useMutation(
    trpc.vendorAssessment.saveDraft.mutationOptions()
  )
  const { mutateAsync: saveDraft } = saveDraftMutation
  const submitMutation = useMutation(
    trpc.vendorAssessment.submit.mutationOptions()
  )
  const reviewMutation = useMutation(
    trpc.vendorAssessment.review.mutationOptions()
  )
  const fastApproveLowMutation = useMutation(
    trpc.vendorAssessment.fastApproveLow.mutationOptions()
  )
  const generateEvidenceMutation = useMutation(
    trpc.vendorAssessment.generateEvidence.mutationOptions()
  )
  const setFieldDispositionMutation = useMutation(
    trpc.vendorAssessment.setFieldDisposition.mutationOptions()
  )
  const [controlPickerOpen, setControlPickerOpen] = useState(false)
  const controlPickerQuery = useQuery({
    ...trpc.riskRegister.listControlsForPicker.queryOptions(),
    enabled: controlPickerOpen,
  })
  const linkedControlMutation = useMutation(trpc.vendorAssessment.setLinkedControl.mutationOptions())
  const handleLinkedControl = async (controlId: string, linked: boolean) => {
    if (!assessmentId || !isEditable || linkedControlMutation.isPending) return
    try {
      await linkedControlMutation.mutateAsync({ assessmentId, controlId, linked })
      await queryClient.invalidateQueries({ queryKey: trpc.vendorAssessment.listControlMappings.queryKey({ assessmentId }) })
      if (linked) setControlPickerOpen(false)
      onUpdate()
    } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not update linked controls') }
  }
  const controlMappingsQuery = useQuery({
    ...trpc.vendorAssessment.listControlMappings.queryOptions({
      assessmentId: assessmentId!,
    }),
    enabled: !!assessmentId,
  })
  // Enrolled-framework citations for the battery rows (CC codes are SOC 2
  // shorthand; HIPAA/ISO workspaces get their own paragraph numbers).
  const controlCitationsQuery = useQuery({
    ...trpc.vendorAssessment.controlCitations.queryOptions(),
    enabled: !!assessmentId,
  })

  const assessment = assessmentQuery.data
  const status = assessment?.status as AssessmentStatus | undefined
  const isEditable = !readOnly && (status === 'DRAFT' || status === 'NEEDS_REVISION')
  const isReviewer = !readOnly && currentUserId === assessment?.reviewerId

  // Tier-aware questionnaire (#963): show deeper-diligence fields only for
  // HIGH/CRITICAL vendors, and HIPAA fields only when HIPAA is enabled.
  const hipaaEnabled = useHipaaEnabled()
  const vendorTier =
    (assessment?.vendor?.riskTier as string | undefined) ?? null
  const evidenceRequests =
    (assessment?.evidenceRequests as EvidenceRequest[] | undefined) ?? []
  const evidenceCompleteness = getEvidenceCompleteness(evidenceRequests)
  const lowFastPathAvailable = isEditable && vendorTier === 'LOW'
  const contextRiskReady = !securityDraft.vendorContext || Boolean((securityDraft.vendorContext as Record<string, unknown>).inherentImpact && (securityDraft.vendorContext as Record<string, unknown>).inherentLikelihood)
  const riskResponse = securityDraft.riskResponse as VendorRiskResponse | undefined
  const responseReady = Boolean(riskResponse?.strategy && riskResponse.rationale?.trim() && (riskResponse.strategy !== 'MITIGATE' || riskResponse.riskIds.length > 0))
  const canFastApproveLow =
    lowFastPathAvailable && contextRiskReady && responseReady && riskResponse?.strategy !== 'AVOID' &&
    riskTierDraft === 'LOW' &&
    summaryDraft.trim().length > 0 &&
    evidenceCompleteness.requiredComplete

  const buildDraftPayload = useCallback(
    (
      overrides?: Partial<{
        securityDraft: Record<string, unknown>
        dataDraft: Record<string, unknown>
        complianceDraft: Record<string, unknown>
        contractualDraft: Record<string, unknown>
        summaryDraft: string
        riskTierDraft: string
      }>
    ) => ({
      securityPosture: overrides?.securityDraft ?? securityDraft,
      dataHandling: overrides?.dataDraft ?? dataDraft,
      complianceCerts: overrides?.complianceDraft ?? complianceDraft,
      contractualReview: overrides?.contractualDraft ?? contractualDraft,
      summary: (overrides?.summaryDraft ?? summaryDraft) || null,
      recommendedRiskTier: (overrides?.riskTierDraft ?? riskTierDraft) || null,
    }),
    [
      securityDraft,
      dataDraft,
      complianceDraft,
      contractualDraft,
      summaryDraft,
      riskTierDraft,
    ]
  )

  /** Seed every local draft (sections, summary, tier, provenance) from a server row. */
  const hydrateFromAssessment = useCallback((fresh: typeof assessment) => {
    if (!fresh) return
    setSecurityDraft({
      ...((fresh.securityPosture as Record<string, unknown>) ?? {}),
      ...(['DRAFT', 'NEEDS_REVISION'].includes(fresh.status)
        ? { vendorContext: { ...VendorAssessmentContextSchema.parse(fresh.vendor ?? {}), ...fresh.securityPosture?.vendorContext } } : {}),
    })
    setDataDraft((fresh.dataHandling as Record<string, unknown>) ?? {})
    setComplianceDraft((fresh.complianceCerts as Record<string, unknown>) ?? {})
    setContractualDraft({
      ...(['DRAFT', 'NEEDS_REVISION'].includes(fresh.status) && fresh.vendor?.contractRenewalDate
        ? { expirationDate: new Date(fresh.vendor.contractRenewalDate).toISOString().slice(0, 10) } : {}),
      ...((fresh.contractualReview as Record<string, unknown>) ?? {}),
    })
    setSummaryDraft(fresh.summary ?? '')
    setRiskTierDraft(fresh.recommendedRiskTier ?? '')
    setProvenance((fresh.fieldProvenance as FieldProvenanceMap | null) ?? {})
  }, [])

  // Initialize local draft from fetched data
  useEffect(() => {
    if (assessment && initializedFor !== assessment.id) {
      hydrateFromAssessment(assessment)
      setInitializedFor(assessment.id)
    }
  }, [assessment, initializedFor, hydrateFromAssessment])

  // Reset when dialog opens with new assessment
  useEffect(() => {
    if (!open) {
      setInitializedFor(null)
      setStageSelection(null)
      setShowSubmitModal(false)
      setShowReviewModal(false)
      setReviewComments('')
      setForceOpenSections(false)
    }
  }, [open, assessmentId])

  // Auto-save debounce ref
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const queueAutoSave = useCallback(
    (
      overrides?: Partial<{
        securityDraft: Record<string, unknown>
        dataDraft: Record<string, unknown>
        complianceDraft: Record<string, unknown>
        contractualDraft: Record<string, unknown>
        summaryDraft: string
        riskTierDraft: string
      }>
    ) => {
      if (!assessment || !isEditable) return
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(async () => {
        const saved = await saveDraft({
          id: assessment.id,
          data: buildDraftPayload(overrides) as any,
        })
        // The server owns provenance (RFC-231 §5 rule 2); its answer wins over
        // the optimistic local clear.
        setProvenance(((saved as typeof assessment)?.fieldProvenance as FieldProvenanceMap | null) ?? {})
        queryClient.invalidateQueries({
          queryKey: trpc.vendorAssessment.getById.queryKey({
            id: assessment.id,
          }),
        })
      }, 1500)
    },
    [assessment, isEditable, buildDraftPayload, saveDraft, queryClient]
  )

  const handleSave = async () => {
    if (!assessment) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    await saveDraft({
      id: assessment.id,
      data: buildDraftPayload() as any,
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vendorAssessment.getById.queryKey({ id: assessment.id }),
    })
    onUpdate()
  }

  // Reviewer / due-date edits from the header facts grid — saved immediately
  // (no draft state to flush), then refetched so the grid shows the result.
  const handleHeaderFactSave = async (
    patch: { reviewerId?: string | null } | { dueAt?: Date | null }
  ) => {
    if (!assessment) return
    try {
      await saveDraft({ id: assessment.id, data: patch as any })
      queryClient.invalidateQueries({
        queryKey: trpc.vendorAssessment.getById.queryKey({ id: assessment.id }),
      })
      onUpdate()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the assessment')
    }
  }

  const handleSubmit = async (reviewerId: string) => {
    if (!assessment) return
    // Save first
    await saveDraft({
      id: assessment.id,
      data: buildDraftPayload() as any,
    })
    await submitMutation.mutateAsync({
      id: assessment.id,
      reviewerId,
      summary: summaryDraft,
      recommendedRiskTier: riskTierDraft as any,
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vendorAssessment.getById.queryKey({ id: assessment.id }),
    })
    setShowSubmitModal(false)
    onUpdate()
  }

  const handleReview = async () => {
    if (!assessment) return
    await reviewMutation.mutateAsync({
      id: assessment.id,
      status: reviewAction,
      comments: reviewComments || undefined,
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vendorAssessment.getById.queryKey({ id: assessment.id }),
    })
    setShowReviewModal(false)
    onUpdate()
  }

  const handleFastApproveLow = async () => {
    if (!assessment) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    try {
      await saveDraft({
        id: assessment.id,
        data: buildDraftPayload() as any,
      })
      await fastApproveLowMutation.mutateAsync({
        id: assessment.id,
      })
      queryClient.invalidateQueries({
        queryKey: trpc.vendorAssessment.getById.queryKey({
          id: assessment.id,
        }),
      })
      toast.success('LOW assessment approved')
      onUpdate()
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to approve assessment'
      )
    }
  }

  const handleGenerateEvidence = async () => {
    if (!assessment || readOnly) return
    await generateEvidenceMutation.mutateAsync({ id: assessment.id })
    queryClient.invalidateQueries({
      queryKey: trpc.vendorAssessment.getById.queryKey({ id: assessment.id }),
    })
    onUpdate()
  }

  const updateSecurity = (key: string, val: unknown) => {
    const nextSecurityDraft = { ...securityDraft, [key]: val }
    setSecurityDraft(nextSecurityDraft)
    queueAutoSave({ securityDraft: nextSecurityDraft })
  }
  const updateData = (key: string, val: unknown) => {
    const nextDataDraft = { ...dataDraft, [key]: val }
    setDataDraft(nextDataDraft)
    queueAutoSave({ dataDraft: nextDataDraft })
  }
  const updateCompliance = (key: string, val: unknown) => {
    const nextComplianceDraft = { ...complianceDraft, [key]: val }
    setComplianceDraft(nextComplianceDraft)
    queueAutoSave({ complianceDraft: nextComplianceDraft })
  }
  const updateContractual = (key: string, val: unknown) => {
    const nextContractualDraft = { ...contractualDraft, [key]: val }
    setContractualDraft(nextContractualDraft)
    queueAutoSave({ contractualDraft: nextContractualDraft })
  }

  // RFC-231 §3.2 — a manual edit clears the chip locally right away; the
  // server drops the entry on the next autosave (saveDraft rule 2).
  const clearLocalProvenance = useCallback((section: string, key: string) => {
    setProvenance((prev) => {
      const prefix = `${section}.${key}`
      const next: FieldProvenanceMap = {}
      for (const [path, entry] of Object.entries(prev)) {
        if (path === prefix || path.startsWith(`${prefix}.`)) continue
        next[path] = entry
      }
      return next
    })
  }, [])
  const withClear =
    (section: string, update: (key: string, val: unknown) => void) =>
    (key: string, val: unknown) => {
      clearLocalProvenance(section, key)
      update(key, val)
    }
  const onSecurityChange = withClear('securityPosture', updateSecurity)
  const onDataChange = withClear('dataHandling', updateData)
  const onContractualChange = withClear('contractualReview', updateContractual)
  const onComplianceChange = (key: string, val: unknown) => {
    if (key === 'soc2') {
      // Only the sub-key that changed loses its chip.
      const before = (complianceDraft.soc2 as Record<string, unknown>) ?? {}
      const after = (val as Record<string, unknown>) ?? {}
      for (const sub of new Set([...Object.keys(before), ...Object.keys(after)])) {
        if (before[sub] !== after[sub]) clearLocalProvenance('complianceCerts', `soc2.${sub}`)
      }
    } else {
      clearLocalProvenance('complianceCerts', key)
    }
    updateCompliance(key, val)
  }




  const handleFieldDisposition = async (path: string, action: 'accept' | 'discard') => {
    if (!assessment) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setBusyPath(path)
    try {
      // Flush the draft first so the server sees the latest values before it
      // protects (accept) or clears (discard) the path.
      await saveDraft({ id: assessment.id, data: buildDraftPayload() as any })
      const updated = await (setFieldDispositionMutation as any).mutateAsync({
        assessmentId: assessment.id,
        path,
        action,
      })
      hydrateFromAssessment(updated as typeof assessment)
      queryClient.setQueryData(
        trpc.vendorAssessment.getById.queryKey({ id: assessment.id }),
        updated
      )
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update the field')
    } finally {
      setBusyPath(null)
    }
  }

  const documentsQuery = useQuery({
    ...trpc.vendorManagement.listDocuments.queryOptions({
      vendorId: assessment?.vendor?.id ?? '',
    }),
    enabled: !!assessment?.vendor?.id,
  })
  const documentLabels = useMemo(() => {
    const labels: Record<string, string> = {}
    for (const doc of (documentsQuery.data ?? []) as Array<{ id: string; documentType: string; title: string; fileName: string | null }>) {
      labels[doc.id] = DOC_TYPE_LABELS[doc.documentType] ?? doc.fileName ?? doc.title
    }
    return labels
  }, [documentsQuery.data])
  // Not memoized on purpose: the tabs re-render on every keystroke anyway, and
  // the handlers must see the live drafts (they flush before the server acts).
  const provenanceContext: ProvenanceContextValue = {
    provenance,
    editable: isEditable,
    busyPath,
    documentLabels,
    onAccept: (path) => void handleFieldDisposition(path, 'accept'),
    onDiscard: (path) => void handleFieldDisposition(path, 'discard'),
  }

  // Work stages organize the existing lifecycle; navigation never submits or approves.
  const provenanceEntries = Object.values(provenance ?? {})
  const unacceptedCount = provenanceEntries.filter((e) => e && !e.accepted).length
  // The markdown editor serializes an empty doc as whitespace — trim before
  // judging the summary present.
  const summaryPresent = Boolean(summaryDraft.trim())
  const decided = Boolean(summaryPresent && riskTierDraft)
  const hasAnswers = [securityDraft, dataDraft, complianceDraft, contractualDraft].some(sectionHasValues)
  const currentStage = ['SUBMITTED', 'APPROVED', 'REJECTED'].includes(status ?? '')
    ? 'decision'
    : status === 'NEEDS_REVISION' ? 'assessment'
    : decided && unacceptedCount === 0 ? 'review'
    : hasAnswers ? 'assessment' : 'open'
  useEffect(() => {
    if (open && initializedFor === assessmentId && stageSelection?.assessmentId !== assessmentId && assessmentId) {
      setStageSelection({ assessmentId, key: initialStage && (initialStage !== 'assessment' || isEditable) ? initialStage : currentStage })
    }
  }, [open, initializedFor, assessmentId, stageSelection, currentStage, isEditable, initialStage])
  const activeStage = stageSelection?.assessmentId === assessmentId ? stageSelection.key : currentStage
  const reviewStages: ReviewStage[] = [
    { key: 'open', label: 'Open' },
    { key: 'documents', label: 'Documents' },
    { key: 'assessment', label: 'Assessment' },
    { key: 'review', label: 'Review' },
    { key: 'decision', label: 'Decision', done: status === 'APPROVED' || status === 'REJECTED' },
  ]
  const activeStageIndex = reviewStages.findIndex(stage => stage.key === activeStage)
  const nextStage = reviewStages[activeStageIndex + 1]
  const assessedContext = securityDraft.vendorContext as Record<string, unknown> | undefined
  const assessedImpact = assessedContext?.inherentImpact as 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH' | undefined
  const assessedLikelihood = assessedContext?.inherentLikelihood as 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH' | undefined

  const showAssessedRisk = ['review', 'decision'].includes(activeStage)
  const matrixAxes = showAssessedRisk ? (securityDraft.assessedRisk as { impact?: string; likelihood?: string } | undefined) ?? {} : { impact: assessedImpact, likelihood: assessedLikelihood }
  const matrixScore = matrixAxes.impact && matrixAxes.likelihood ? computeRiskScore(matrixAxes.likelihood, matrixAxes.impact, assessment?.scoringVersion ?? 2) : null
  const matrixRating = matrixAxes.impact && matrixAxes.likelihood ? deriveRating(matrixAxes.likelihood, matrixAxes.impact, assessment?.scoringVersion ?? 2) : ''

  const reviewerFacts = assessment && <>
              <DetailStatusItem label='Initiated by'>
                <span className='font-body text-muted-foreground text-[13px]'>
                  {assessment.initiatedByName ?? 'Unknown'}
                </span>
              </DetailStatusItem>
              <DetailStatusItem label='Reviewer'>
                {isEditable ? (
                  <Select
                    value={assessment.reviewerId ?? '_unassigned'}
                    onValueChange={(v) => void handleHeaderFactSave({ reviewerId: v === '_unassigned' ? null : v })}
                  >
                    <SelectTrigger
                      aria-label='Assign reviewer'
                      className='border-border-divider h-9 w-full max-w-[160px] rounded-sm text-[12px]'
                    >
                      <SelectValue placeholder='Unassigned' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='_unassigned'>Unassigned</SelectItem>
                      {((eligibleReviewersQuery.data?.reviewers ?? []) as EligibleReviewer[]).map((r) => (
                        <SelectItem key={r.userId} value={r.userId}>
                          {r.name ?? r.email ?? r.userId}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className='font-body text-muted-foreground text-[13px]'>
                    {assessment.reviewerName ?? 'Unassigned'}
                  </span>
                )}
              </DetailStatusItem>
  </>

  if (!assessmentId) return null

  return (
    <>
      <AssessmentShell
        mode={mode}
        open={open}
        onOpenChange={onOpenChange}
        title={
          assessment
            ? mode === 'page' ? `${assessment.vendorName} assessment` : `Assessment v${assessment.version}`
            : 'Vendor Assessment'
        }
        code={
          assessment
            ? `${assessment.vendorDisplayId}-ASM-${assessment.version}`
            : null
        }
        statusBadge={status ? <AssessmentStatusBadge status={status} /> : null}
        vendorName={assessment?.vendorName ?? null}
        facts={
          assessment ? (
            <div role='group' aria-label={mode === 'inline' ? 'Vendor metadata' : 'Assessment metadata'}><DetailStatusGrid className={`mt-6 !max-w-none grid-cols-2 gap-x-8 gap-y-4 ${mode === 'inline' ? 'lg:grid-cols-4' : 'sm:grid-cols-3 lg:grid-cols-5'} [&>div>:last-child]:flex [&>div>:last-child]:h-9 [&>div>:last-child]:items-center`}>
              {mode === 'inline' ? headerFields : <>
              <DetailStatusItem label='Status'>
                <div>{status ? <AssessmentStatusBadge status={status} /> : null}</div>
              </DetailStatusItem>
              {reviewerFacts}</>}
              <DetailStatusItem label='Due'>
                {isEditable ? (
                  <DatePicker
                    value={assessment.dueAt ? new Date(assessment.dueAt) : undefined}
                    onChange={(date) => void handleHeaderFactSave({ dueAt: date ?? null })}
                    placeholder='Set date'
                    className='border-border-divider h-9 w-full max-w-[160px] rounded-sm text-[12px]'
                  />
                ) : (
                  <span className='font-body text-muted-foreground text-[13px]'>
                    {assessment.dueAt
                      ? new Date(assessment.dueAt).toLocaleDateString()
                      : '—'}
                  </span>
                )}
              </DetailStatusItem>
              <DetailStatusItem label={mode !== 'inline' && assessment.completedAt ? 'Completed' : 'Submitted'}>
                <span className='font-body text-muted-foreground text-[13px]'>
                  {mode !== 'inline' && assessment.completedAt
                    ? new Date(assessment.completedAt).toLocaleDateString()
                    : assessment.submittedAt
                      ? new Date(assessment.submittedAt).toLocaleDateString()
                      : '—'}
                </span>
              </DetailStatusItem>
            </DetailStatusGrid></div>
          ) : null
        }
        banners={
          <>
            {/* The Status grid already shows the state, reviewer and date —
                the banner earns its place only when there's a review note. */}
            {status === 'APPROVED' && assessment?.reviewComments && (
              <div className='bg-status-green-bg mt-2 flex items-center gap-2 rounded-sm px-3 py-2'>
                <CheckCircle2 className='text-status-green h-3.5 w-3.5 shrink-0' />
                <span className='font-body text-status-green text-[11px]'>
                  {assessment.reviewComments}
                </span>
              </div>
            )}
            {status === 'REJECTED' && assessment?.reviewComments && (
              <div className='bg-status-red-bg mt-2 flex items-center gap-2 rounded-sm px-3 py-2'>
                <XCircle className='text-status-red h-3.5 w-3.5 shrink-0' />
                <span className='font-body text-status-red text-[11px]'>
                  Rejected
                  {assessment?.reviewComments
                    ? `: ${assessment.reviewComments}`
                    : ''}
                </span>
              </div>
            )}
            {status === 'NEEDS_REVISION' && assessment?.reviewComments && (
              <div className='bg-status-blue-bg mt-2 flex items-center gap-2 rounded-sm px-3 py-2'>
                <AlertTriangle className='h-3.5 w-3.5 shrink-0 text-cobalt-strong' />
                <span className='font-body text-[11px] text-cobalt-strong'>
                  Revision requested
                  {assessment?.reviewComments
                    ? `: ${assessment.reviewComments}`
                    : ''}
                </span>
              </div>
            )}
            {status === 'SUBMITTED' && !isReviewer && (
              <div className='bg-status-orange-bg mt-2 flex items-center gap-2 rounded-sm px-3 py-2'>
                <Clock className='text-status-orange h-3.5 w-3.5 shrink-0' />
                <span className='font-body text-status-orange text-[11px]'>
                  Pending review by {assessment?.reviewerName ?? 'reviewer'}
                </span>
              </div>
            )}
          </>
        }
        children={
          <>
          {assessmentQuery.isLoading ? (
            <div className='space-y-5 py-5'>
              <div className='space-y-2'>
                <Skeleton className='h-4 w-32 rounded-sm' />
                <Skeleton className='h-9 w-full rounded-sm' />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <Skeleton className='h-9 rounded-sm' />
                <Skeleton className='h-9 rounded-sm' />
              </div>
              <div className='space-y-2'>
                <Skeleton className='h-4 w-40 rounded-sm' />
                <Skeleton className='h-20 w-full rounded-sm' />
              </div>
              <div className='space-y-2'>
                <Skeleton className='h-4 w-28 rounded-sm' />
                <Skeleton className='h-16 w-full rounded-sm' />
              </div>
            </div>
          ) : assessmentQuery.isError ? (
            <div role='alert' className='space-y-3 py-6'>
              <p>Could not load this assessment.</p>
              <Button variant='outline' onClick={() => void assessmentQuery.refetch()}>Retry</Button>
            </div>
          ) : (
            <div className={`space-y-6 pb-4 ${mode === 'inline' ? 'pt-8' : 'pt-4'}`}>
              {assessment && (
                <AssessmentStageRail stages={reviewStages} currentStage={currentStage} selectedStage={activeStage}
                  onSelect={(key) => setStageSelection({ assessmentId: assessment.id, key })} />
              )}
              <div className={mode !== 'dialog' ? 'grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_300px]' : ''}>
              <div className='min-w-0 space-y-6'>
              <section hidden={activeStage !== 'assessment'} aria-label='Vendor assessment answers' className='space-y-6'>
              <SectionHeading>Vendor practices</SectionHeading>
              <p className='text-text-subtle text-sm'>Review answers drafted from documents, or enter what you know. Your edits are saved to this assessment.</p>
              {vendorTier && !isHighRiskTier(vendorTier) && (
                <p className='text-text-subtle text-xs'>Core diligence questions are shown for this {vendorTier.toLowerCase()}-risk vendor. Deeper questions appear when needed.</p>
              )}
              <section className='space-y-4'>
                <SectionHeading>Risk context</SectionHeading>
                <p className='text-text-subtle text-xs'>These answers belong to this assessment. Set inherent impact and likelihood before approval. Changes update the vendor record only after approval.</p>
                <div className='grid gap-4 sm:grid-cols-2'>
                  {([
                    ['inherentImpact', 'Inherent impact', { VERY_LOW: 'Very low', LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', VERY_HIGH: 'Very high' }],
                    ['inherentLikelihood', 'Inherent likelihood', { VERY_LOW: 'Very low', LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', VERY_HIGH: 'Very high' }],
                    ['dataProcessingRole', 'Subprocessor for customer personal data', Object.fromEntries(VENDOR_SUBPROCESSOR_OPTIONS.filter(option => option.value !== '_unknown').map(option => [option.value, option.label]))],
                  ] as [string, string, Record<string, string>][]).map(([key, label, options]) => {
                    const context = (securityDraft.vendorContext ?? {}) as Record<string, unknown>
                    const value = context[key]
                    return <div key={key} className='space-y-1'>
                      <Label className={cn(DETAIL_FIELD_LABEL_CLASS, 'mb-1.5')} htmlFor={`assessment-context-${key}`}>{label}</Label>
                      {isEditable ? <Select value={value == null || !(String(value) in options) ? '' : String(value)}
                        onValueChange={(next) => onSecurityChange('vendorContext', { ...context,
                          [key]: next === '_unknown' ? null : ['customerDataInvolved', 'containsEphi'].includes(key) ? next === 'true' : next,
                        })}>
                        <SelectTrigger id={`assessment-context-${key}`} fullWidth><SelectValue placeholder={key === 'dataProcessingRole' ? 'Unknown' : 'Select rating'} /></SelectTrigger>
                        <SelectContent>
                          {key === 'dataProcessingRole' && <SelectItem value='_unknown'>Unknown</SelectItem>}
                          {Object.entries(options).map(([option, text]) => <SelectItem key={option} value={option}>{text}</SelectItem>)}
                        </SelectContent>
                      </Select> : <ReadonlyText value={value == null ? null : options[String(value)]} />}
                    </div>
                  })}
                </div>
              </section>

              <ProvenanceContext.Provider value={provenanceContext}>
              {hipaaEnabled && <HipaaBusinessAssociateFields context={(securityDraft.vendorContext ?? {}) as Record<string, unknown>} editable={isEditable} onChange={context => onSecurityChange('vendorContext', context)} />}
              <CollapsibleSection
                title='Security'
                hasValues={sectionHasValues(securityDraft)}
                draftedCount={countDrafted(provenance, 'securityPosture')}
                forceOpen={forceOpenSections}
                editable={isEditable}
              >
                <SecurityTab
                  data={securityDraft}
                  editable={isEditable}
                  onChange={onSecurityChange}
                  vendorTier={vendorTier}
                  hipaaEnabled={hipaaEnabled}
                />
              </CollapsibleSection>
              <CollapsibleSection
                title='Data Handling'
                hasValues={sectionHasValues(dataDraft)}
                draftedCount={countDrafted(provenance, 'dataHandling')}
                forceOpen={forceOpenSections}
                editable={isEditable}
              >
                <DataHandlingTab
                  data={dataDraft}
                  editable={isEditable}
                  onChange={onDataChange}
                  customerDataInvolved={(securityDraft.vendorContext as Record<string, unknown> | undefined)?.customerDataInvolved as boolean | undefined}
                  onCustomerDataChange={(involved, dataTypes) => {
                    const nextData = { ...dataDraft, dataTypes }
                    const nextSecurity = { ...securityDraft, vendorContext: { ...(securityDraft.vendorContext as Record<string, unknown> ?? {}), customerDataInvolved: involved } }
                    clearLocalProvenance('dataHandling', 'dataTypes')
                    clearLocalProvenance('securityPosture', 'vendorContext.customerDataInvolved')
                    setDataDraft(nextData)
                    setSecurityDraft(nextSecurity)
                    queueAutoSave({ dataDraft: nextData, securityDraft: nextSecurity })
                  }}
                  vendorTier={vendorTier}
                  hipaaEnabled={hipaaEnabled}
                />
              </CollapsibleSection>
              <CollapsibleSection
                title='Compliance'
                hasValues={sectionHasValues(complianceDraft)}
                draftedCount={countDrafted(provenance, 'complianceCerts')}
                forceOpen={forceOpenSections}
                editable={isEditable}
              >
                <ComplianceTab
                  data={complianceDraft}
                  editable={isEditable}
                  onChange={onComplianceChange}
                  vendorTier={vendorTier}
                  hipaaEnabled={hipaaEnabled}
                />
              </CollapsibleSection>
              <CollapsibleSection
                title='Contractual'
                hasValues={sectionHasValues(contractualDraft)}
                draftedCount={countDrafted(provenance, 'contractualReview')}
                forceOpen={forceOpenSections}
                editable={isEditable}
              >
                <ContractualTab
                  data={contractualDraft}
                  editable={isEditable}
                  onChange={onContractualChange}
                  vendorTier={vendorTier}
                  hipaaEnabled={hipaaEnabled}
                />
              </CollapsibleSection>
              </ProvenanceContext.Provider>

              </section>
              <section hidden={activeStage !== 'open'} aria-label='Vendor setup' className='space-y-6'>
                {intake ?? <div className='space-y-3'><SectionHeading>Vendor details</SectionHeading><p className='text-sm'>{assessment?.vendorName}</p></div>}
                <VendorUsageFields context={(securityDraft.vendorContext ?? {}) as Record<string, unknown>} editable={isEditable}
                  onChange={patch => onSecurityChange('vendorContext', { ...(securityDraft.vendorContext as Record<string, unknown> ?? {}), ...patch })} />
              </section>
              <section hidden={activeStage !== 'documents'} aria-label='Assessment documents' className='space-y-6'>
                <SectionHeading>Documents</SectionHeading>
                {documents}
                <p className='text-text-subtle text-sm'>Add supporting documents if available, then start assessment with Vera. Without documents, Vera uses the vendor details and your answers and highlights missing evidence.</p>
              {assessmentId && assessment?.vendor?.id && (
                <VendorAssessmentInputsPanel
                  assessmentId={assessmentId}
                  vendorId={assessment.vendor.id}
                  vendorName={assessment.vendorName ?? undefined}
                  isEditable={isEditable}
                  aiEnabled={vendorAiAssessmentFlag.enabled}
                  assessmentActionContainer={['documents', 'assessment', 'review'].includes(activeStage) ? assessmentActionContainer : null}
                  assessmentActionLabel={activeStage === 'documents' ? 'Start assessment' : 'Reassess with Vera'}
                  onBeforeAssess={handleSave}
                  evidenceRequests={evidenceRequests}
                  onChanged={() => {
                    if (!assessmentId) return
                    void queryClient.invalidateQueries({
                      queryKey: trpc.vendorAssessment.getById.queryKey({
                        id: assessmentId,
                      }),
                    })
                  }}
                  onAssessed={async () => {
                    // Batch AI assess overwrote the sections server-side. The
                    // one-time initialization guard won't re-seed the form
                    // drafts, so refetch getById and re-hydrate explicitly.
                    await queryClient.invalidateQueries({
                      queryKey: trpc.vendorAssessment.getById.queryKey({
                        id: assessmentId,
                      }),
                    })
                    const fresh = queryClient.getQueryData(
                      trpc.vendorAssessment.getById.queryKey({ id: assessmentId })
                    ) as typeof assessment
                    hydrateFromAssessment(fresh)
                    setForceOpenSections(true)
                    setStageSelection({ assessmentId, key: activeStage === 'documents' ? 'assessment' : activeStage })
                    queryClient.invalidateQueries({
                      queryKey:
                        trpc.vendorAssessment.listControlMappings.queryKey({
                          assessmentId,
                        }),
                    })
                    queryClient.invalidateQueries({
                      queryKey: trpc.vendorAssessment.listInputs.queryKey({
                        assessmentId,
                      }),
                    })
                    onUpdate?.()
                  }}
                />
              )}

              </section>
              <section hidden={activeStage !== 'review'} aria-label='Assessment review' className='space-y-6'>
              <section id='stage-summary' className='scroll-mt-4 space-y-4'>
                <SectionHeading>Summary</SectionHeading>
                <SummaryTab
                  summary={summaryDraft}
                  riskTier={riskTierDraft}
                  showRating={mode === 'dialog'}
                  editable={isEditable}
                  onSummaryChange={(v) => {
                    setSummaryDraft(v)
                    queueAutoSave({ summaryDraft: v })
                  }}
                  assessedRisk={securityDraft.assessedRisk as { impact?: string; likelihood?: string } | undefined}
                  onAssessedRiskChange={(key, value) => {
                    const assessedRisk = { ...(securityDraft.assessedRisk as Record<string, string> ?? {}), [key]: value }
                    const nextSecurity = { ...securityDraft, assessedRisk }
                    const tier = assessedRisk.impact && assessedRisk.likelihood ? deriveRating(assessedRisk.likelihood, assessedRisk.impact) : ''
                    setSecurityDraft(nextSecurity)
                    setRiskTierDraft(tier)
                    queueAutoSave({ securityDraft: nextSecurity, riskTierDraft: tier })
                  }}
                />
              </section>
              <section id='stage-controls' className='scroll-mt-4 space-y-4'>
                <div className='flex items-center justify-between gap-3'>
                  <SectionHeading>Controls</SectionHeading>
                  {isEditable && <Button variant='outline' size='sm' disabled={linkedControlMutation.isPending || controlMappingsQuery.isLoading || controlMappingsQuery.isError} onClick={() => setControlPickerOpen(true)}><Plus className='size-3.5' />Link controls</Button>}
                </div>
                {controlMappingsQuery.isError ? <div role='alert' className='space-y-2 text-sm'>Could not load linked controls. <Button variant='outline' size='sm' onClick={() => void controlMappingsQuery.refetch()}>Retry</Button></div> : <ControlsTab
                  mappings={controlMappingsQuery.data ?? []}
                  isLoading={controlMappingsQuery.isLoading}
                  citationInfo={controlCitationsQuery.data}
                  onUnlink={isEditable ? controlId => void handleLinkedControl(controlId, false) : undefined}
                  busy={linkedControlMutation.isPending}
                />}
                <PickerDialog open={controlPickerOpen && isEditable} onOpenChange={setControlPickerOpen}
                  title='Link controls' description='Choose a workspace control to include in this assessment.'
                  items={controlPickerQuery.data ?? []}
                  excludedIds={new Set((controlPickerQuery.data ?? []).filter(control => (controlMappingsQuery.data ?? []).some(mapping => mapping.controlId === control.controlId)).map(control => control.id))}
                  searchPlaceholder='Search by code or title...' emptyMessage={controlPickerQuery.isLoading ? 'Loading controls...' : 'No available controls.'}
                  itemSearchText={control => `${control.displayCode ?? ''} ${control.title}`}
                  renderRow={control => <span>{control.displayCode && <span className='mr-2 font-medium text-cobalt-strong'>{control.displayCode}</span>}{control.title}</span>}
                  onSelect={control => void handleLinkedControl(control.controlId, true)}
                  footer={controlPickerQuery.isError ? <div role='alert' className='px-6 pb-4 text-sm'>Could not load controls. <Button variant='outline' size='sm' onClick={() => void controlPickerQuery.refetch()}>Retry</Button></div> : undefined} />
              </section>

              </section>
              <section hidden={activeStage !== 'decision'} aria-label='Assessment decision' className='space-y-6'>
                <SectionHeading>Decision</SectionHeading>
                <p className='text-text-subtle text-sm'>
                  {status === 'APPROVED' ? 'This assessment is approved. Its answers and recommendation are read-only.'
                    : status === 'REJECTED' ? 'This assessment was rejected. Review the decision and reviewer comments above.'
                    : status === 'SUBMITTED' ? isReviewer ? 'Review the answers, control coverage, and recommendation before recording your decision.' : 'This assessment is awaiting the assigned reviewer’s decision.'
                    : 'Review your recommendation, then submit this assessment for approval.'}
                </p>
                {isEditable && !contextRiskReady && <p className='text-sm text-status-orange'>Set inherent impact and likelihood under Assessment before submitting for approval.</p>}
                {isEditable ? <VendorRiskResponseFields value={riskResponse} vendorId={assessment.vendorId ?? assessment.vendor?.id ?? ''} onChange={value => onSecurityChange('riskResponse', value)} /> : <VendorRiskResponseReadOnly value={riskResponse} />}
                <SummaryTab summary={summaryDraft} riskTier={riskTierDraft} showRating={mode === 'dialog'} editable={false}
                  onSummaryChange={() => {}} assessedRisk={securityDraft.assessedRisk as { impact?: string; likelihood?: string } | undefined} />
              </section>
              </div>
              {mode !== 'dialog' && assessment?.vendor && (
                <aside aria-label='Vendor risk context' className='space-y-6 lg:sticky lg:top-4'>
                  <div className='space-y-3'>
                    <SectionHeading>Risk score</SectionHeading>
                    <div className='mt-6 flex items-center gap-4'>
                      <p aria-label={showAssessedRisk ? 'Assessed risk score' : 'Inherent risk score'} className={`text-7xl font-light tracking-tighter ${riskRatingSignal(matrixRating).className}`}>{matrixScore ?? '—'}</p>
                      <p className='text-xs text-muted-foreground'>{showAssessedRisk ? 'Assessed risk' : 'Inherent risk'}</p>
                    </div>
                    {matrixAxes.impact && matrixAxes.likelihood ? <>
                      <p className='text-text-subtle text-xs'>Impact: {formatTierLabel(matrixAxes.impact)} · Likelihood: {formatTierLabel(matrixAxes.likelihood)}</p>
                      <RiskMatrixCard counts={[]} title='' scoringVersion={assessment.scoringVersion ?? 2} markerName={showAssessedRisk ? 'assessed risk' : 'inherent risk'} marker={{ likelihood: matrixAxes.likelihood, impact: matrixAxes.impact }} />
                    </> : <p className='text-text-subtle text-xs'>{showAssessedRisk ? 'Set assessed impact and likelihood in Review to see the risk matrix.' : 'Set inherent impact and likelihood in Assessment to see the risk matrix.'}</p>}
                  </div>
                </aside>
              )}
              </div>
            </div>
          )}

          </>
        }
        footer={
          <div className={mode !== 'dialog' ? 'grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]' : ''}>
          <div role='group' aria-label='Assessment actions' className='flex min-w-0 flex-wrap items-center justify-between gap-3 py-5'>
          <div className='flex items-center gap-3'>
            {['assessment', 'review'].includes(activeStage) && isEditable && <span ref={setAssessmentActionContainer} />}
            {saveDraftMutation.isPending && (
              <span className='font-body text-text-placeholder text-[11px]'>
                Saving...
              </span>
            )}
            {saveDraftMutation.isSuccess && (
              <span className='font-body text-status-green text-[11px]'>
                Saved
              </span>
            )}
          </div>

          <div className='grid grid-cols-2 gap-2 sm:auto-cols-fr sm:grid-flow-col sm:grid-cols-none [&>button]:h-8 [&>button]:w-full [&>button]:rounded-sm [&>button]:text-[12px]'>
            {activeStageIndex > 0 && <Button variant='outline' size='sm' onClick={() => setStageSelection({ assessmentId, key: reviewStages[activeStageIndex - 1].key })}>Back</Button>}
            {/* DRAFT / NEEDS_REVISION actions */}
            {isEditable && (
              <>
                <Button
                  variant='outline'
                  size='sm'
                  className='border-border-divider h-8 rounded-sm text-[12px]'
                  onClick={handleSave}
                  disabled={saveDraftMutation.isPending}
                >
                  Save Draft
                </Button>
                {/* Low-risk fast path (RFC-196): shown only once it's
                      actionable, so it never appears as a confusing disabled
                      button. Approves without routing to a separate reviewer. */}
                {activeStage === 'decision' && canFastApproveLow && (
                  <Button
                    size='sm'
                    variant='outline' className='h-8 rounded-sm text-[12px]'
                    onClick={handleFastApproveLow}
                    disabled={
                      saveDraftMutation.isPending ||
                      fastApproveLowMutation.isPending
                    }
                    tooltip='Low-risk vendor. Approve without a separate reviewer.'
                  >
                    {fastApproveLowMutation.isPending
                      ? 'Approving...'
                      : 'Approve (Low risk)'}
                  </Button>
                )}
                {activeStage === 'decision' && (
                <Button
                  size='sm'
                  className='bg-primary text-primary-foreground hover:bg-primary/90 h-8 rounded-sm text-[12px]'
                  onClick={() => setShowSubmitModal(true)}
                  disabled={!summaryPresent || !riskTierDraft || !contextRiskReady || !responseReady || saveDraftMutation.isPending}
                >
                  Submit for Approval
                </Button>
                )}
              </>
            )}

            {/* SUBMITTED + is reviewer actions */}
            {activeStage === 'decision' && status === 'SUBMITTED' && isReviewer && (
              <>
                {riskResponse?.strategy === 'AVOID' && <Button
                  size='sm'
                  variant='outline' className='h-8 rounded-sm text-[12px] text-destructive'
                  onClick={() => {
                    setReviewAction('REJECTED')
                    setShowReviewModal(true)
                  }}
                >
                  Reject
                </Button>}
                <Button
                  size='sm'
                  variant='outline' className='h-8 rounded-sm text-[12px]'
                  onClick={() => {
                    setReviewAction('NEEDS_REVISION')
                    setShowReviewModal(true)
                  }}
                >
                  Request Revision
                </Button>
                <Button
                  disabled={riskResponse?.strategy === 'AVOID'}
                  size='sm'
                  className='h-8 rounded-sm text-[12px]'
                  onClick={() => {
                    setReviewAction('APPROVED')
                    setShowReviewModal(true)
                  }}
                >
                  Approve
                </Button>
              </>
            )}

            {/* APPROVED actions */}
            {!readOnly && activeStage === 'decision' && status === 'APPROVED' && (
              <Button
                size='sm'
                className='bg-primary text-primary-foreground hover:bg-primary/90 h-8 rounded-sm text-[12px]'
                onClick={handleGenerateEvidence}
                disabled={
                  generateEvidenceMutation.isPending ||
                  !!assessment?.evidencePackId
                }
              >
                <FileText className='h-3.5 w-3.5' />
                {assessment?.evidencePackId
                  ? 'Evidence Generated'
                  : generateEvidenceMutation.isPending
                    ? 'Generating...'
                    : 'Generate Evidence'}
              </Button>
            )}
            {activeStage === 'documents' && isEditable && <span ref={setAssessmentActionContainer} />}
            {assessment && nextStage && !(activeStage === 'documents' && isEditable) && <Button size='sm' variant={activeStage === 'documents' ? 'outline' : 'default'}
              onClick={() => setStageSelection({ assessmentId, key: nextStage.key })}>
              {`Continue to ${nextStage.label.toLowerCase()}`}
            </Button>}
          </div>
          </div>
          </div>
        }
      />

      {/* Submit Modal */}
      {showSubmitModal && (
        <SubmitForApprovalModal
          reviewers={eligibleReviewersQuery.data?.reviewers ?? []}
          allowSelfReview={
            eligibleReviewersQuery.data?.allowSelfReview ?? false
          }
          enforceSeparation={
            eligibleReviewersQuery.data?.enforceSeparation ?? false
          }
          isLoading={eligibleReviewersQuery.isLoading}
          currentUserId={currentUserId}
          workspaceId={workspaceId}
          onSubmit={handleSubmit}
          onClose={() => setShowSubmitModal(false)}
          isPending={submitMutation.isPending}
        />
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal
          action={reviewAction}
          comments={reviewComments}
          onCommentsChange={setReviewComments}
          onConfirm={handleReview}
          onClose={() => setShowReviewModal(false)}
          isPending={reviewMutation.isPending}
        />
      )}
    </>
  )
}

// ── Security Tab ──

function SecurityTab({
  data,
  editable,
  onChange,
  vendorTier,
  hipaaEnabled,
}: {
  data: Record<string, unknown>
  editable: boolean
  onChange: (key: string, val: unknown) => void
  vendorTier: string | null
  hipaaEnabled: boolean
}) {
  const show = makeFieldVisibility(data, vendorTier, hipaaEnabled)
  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-2 gap-4 lg:grid-cols-3'>
        <div>
          <FieldLabel path='securityPosture.encryptionAtRest'>Encryption at Rest</FieldLabel>
          {editable ? (
            <BoolSelect
              value={data.encryptionAtRest as boolean | null}
              onChange={(v) => onChange('encryptionAtRest', v)}
            />
          ) : (
            <ReadonlyBool value={data.encryptionAtRest as boolean | null} />
          )}
        </div>
        <div>
          <FieldLabel path='securityPosture.encryptionInTransit'>Encryption in Transit</FieldLabel>
          {editable ? (
            <BoolSelect
              value={data.encryptionInTransit as boolean | null}
              onChange={(v) => onChange('encryptionInTransit', v)}
            />
          ) : (
            <ReadonlyBool value={data.encryptionInTransit as boolean | null} />
          )}
        </div>
        <div>
          <FieldLabel path='securityPosture.mfaEnforced'>MFA Enforced</FieldLabel>
          {editable ? (
            <BoolSelect
              value={data.mfaEnforced as boolean | null}
              onChange={(v) => onChange('mfaEnforced', v)}
            />
          ) : (
            <ReadonlyBool value={data.mfaEnforced as boolean | null} />
          )}
        </div>
        <div>
          <FieldLabel path='securityPosture.vulnerabilityScanning'>Vulnerability Scanning</FieldLabel>
          {editable ? (
            <BoolSelect
              value={data.vulnerabilityScanning as boolean | null}
              onChange={(v) => onChange('vulnerabilityScanning', v)}
            />
          ) : (
            <ReadonlyBool
              value={data.vulnerabilityScanning as boolean | null}
            />
          )}
        </div>
        <div>
          <FieldLabel path='securityPosture.incidentResponsePlan'>Incident Response Plan</FieldLabel>
          {editable ? (
            <BoolSelect
              value={data.incidentResponsePlan as boolean | null}
              onChange={(v) => onChange('incidentResponsePlan', v)}
            />
          ) : (
            <ReadonlyBool value={data.incidentResponsePlan as boolean | null} />
          )}
        </div>
        {show('penTestFrequency') && (
          <div>
            <FieldLabel path='securityPosture.penTestFrequency'>Pen Test Frequency</FieldLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(data.penTestFrequency as string) ?? ''}
                onChange={(e) => onChange('penTestFrequency', e.target.value)}
                placeholder='e.g. Annual, Bi-annual'
              />
            ) : (
              <ReadonlyText value={data.penTestFrequency as string} />
            )}
          </div>
        )}
      </div>
      <div className='grid grid-cols-2 gap-4 lg:grid-cols-3'>
        {show('lastPentestDate') && (
          <div>
            <FieldLabel path='securityPosture.lastPentestDate'>Last Pen Test</FieldLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(data.lastPentestDate as string) ?? ''}
                onChange={(e) => onChange('lastPentestDate', e.target.value)}
                placeholder='YYYY-MM'
              />
            ) : (
              <ReadonlyText value={data.lastPentestDate as string} />
            )}
          </div>
        )}
        {show('hostingProvider') && (
          <div>
            <FieldLabel path='securityPosture.hostingProvider'>Hosting Provider</FieldLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(data.hostingProvider as string) ?? ''}
                onChange={(e) => onChange('hostingProvider', e.target.value)}
                placeholder='e.g. AWS us-east-1'
              />
            ) : (
              <ReadonlyText value={data.hostingProvider as string} />
            )}
          </div>
        )}
      </div>
      {show('accessControlModel') && (
        <div>
          <FieldLabel path='securityPosture.accessControlModel'>Access Control Model</FieldLabel>
          {editable ? (
            /* Extraction writes a full sentence here, not just "RBAC" — give
               it room to wrap (matches the Notes fields' textarea sizing). */
            <Textarea
              className='border-border-divider min-h-[56px] rounded-sm text-[11px]'
              value={(data.accessControlModel as string) ?? ''}
              onChange={(e) => onChange('accessControlModel', e.target.value)}
              placeholder='e.g. RBAC with least privilege, quarterly access reviews'
            />
          ) : (
            <ReadonlyText value={data.accessControlModel as string} />
          )}
        </div>
      )}
      <div>
        <FieldLabel path='securityPosture.notes'>Notes</FieldLabel>
        {editable ? (
          <Textarea
            className='border-border-divider min-h-[80px] rounded-sm text-[11px]'
            value={(data.notes as string) ?? ''}
            onChange={(e) => onChange('notes', e.target.value)}
            placeholder='Security notes...'
          />
        ) : (
          <ReadonlyText value={data.notes as string} multiline />
        )}
      </div>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        {[
          ['encryptionStandard', 'Encryption standard'],
          ['keyManagement', 'Key management'],
          ['mfaMethod', 'MFA method'],
          ['ssoSupported', 'SSO supported'],
          ['vulnerabilityScanningFrequency', 'Vulnerability scanning frequency'],
          ['bugBountyProgram', 'Bug bounty program'],
          ['incidentNotificationSla', 'Incident notification SLA'],
          ['datacenters', 'Data centers'],
        ].map(([key, label]) => hasFieldValue(data[key]) && (
          <div key={key}><FieldLabel path={`securityPosture.${key}`}>{label}</FieldLabel>{typeof data[key] === 'boolean' ? <ReadonlyBool value={data[key] as boolean} /> : <ReadonlyText value={Array.isArray(data[key]) ? (data[key] as string[]).join(', ') : String(data[key])} multiline />}</div>
        ))}
      </div>
    </div>
  )
}

// ── Data Handling Tab ──

function DataHandlingTab({
  data,
  editable,
  onChange,
  vendorTier,
  hipaaEnabled,
  customerDataInvolved,
  onCustomerDataChange,
}: {
  data: Record<string, unknown>
  editable: boolean
  onChange: (key: string, val: unknown) => void
  vendorTier: string | null
  customerDataInvolved?: boolean
  onCustomerDataChange?: (involved: boolean, dataTypes: string[]) => void
  hipaaEnabled: boolean
}) {
  const show = makeFieldVisibility(data, vendorTier, hipaaEnabled)
  return (
    <div className='space-y-4'>
      {/* Short/select fields first in a compact grid; the prose
          textareas follow full-width. */}
      <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
        <div>
          <FieldLabel path='dataHandling.dataClassification'>Data Classification</FieldLabel>
          {editable ? (
            /* Canonical ladder shared with Assets (CLASSIFICATION_LABELS).
               Older AI extractions may hold free text — keep it selectable
               rather than silently dropping the value. */
            <Select
              value={(data.dataClassification as string) || '_unset'}
              onValueChange={(v) =>
                onChange('dataClassification', v === '_unset' ? null : v)
              }
            >
              <SelectTrigger className='border-border-divider h-8 w-full rounded-sm text-[11px]'>
                <SelectValue placeholder='Select classification' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='_unset'>Not set</SelectItem>
                {Object.entries(CLASSIFICATION_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
                {Boolean(data.dataClassification) &&
                  !(data.dataClassification as string in CLASSIFICATION_LABELS) && (
                    <SelectItem value={data.dataClassification as string}>
                      {data.dataClassification as string}
                    </SelectItem>
                  )}
              </SelectContent>
            </Select>
          ) : (
            <ReadonlyText
              value={
                CLASSIFICATION_LABELS[(data.dataClassification as string) ?? ''] ??
                (data.dataClassification as string)
              }
            />
          )}
        </div>
        {show('crossBorderTransfer') && (
          <div>
            <FieldLabel path='dataHandling.crossBorderTransfer'>Cross-Border Transfer</FieldLabel>
            {editable ? (
              <BoolSelect
                value={data.crossBorderTransfer as boolean | null}
                onChange={(v) => onChange('crossBorderTransfer', v)}
              />
            ) : (
              <ReadonlyBool
                value={data.crossBorderTransfer as boolean | null}
              />
            )}
          </div>
        )}
        <div>
          <FieldLabel path='dataHandling.dpaInPlace'>DPA In Place</FieldLabel>
          {editable ? (
            <BoolSelect
              value={data.dpaInPlace as boolean | null}
              onChange={(v) => onChange('dpaInPlace', v)}
              trueLabel='Yes'
              falseLabel='No'
            />
          ) : (
            <ReadonlyBool value={data.dpaInPlace as boolean | null} />
          )}
        </div>
        {show('ephiInvolved') && data.ephiInvolved != null && (
          <div>
            <FieldLabel path='dataHandling.ephiInvolved'>Document-reported ePHI handling</FieldLabel>
            <ReadonlyBool value={data.ephiInvolved as boolean | null} />
            <p className='text-text-subtle mt-1 text-[11px]'>Extracted evidence. Confirm your use in Processes ePHI above.</p>
          </div>
        )}
        {show('baaAvailable') && (
          <div>
            <FieldLabel path='dataHandling.baaAvailable'>BAA Available</FieldLabel>
            {editable ? (
              <BoolSelect value={data.baaAvailable as boolean | null} onChange={(v) => onChange('baaAvailable', v)} />
            ) : (
              <ReadonlyBool value={data.baaAvailable as boolean | null} />
            )}
          </div>
        )}
      </div>
      <div>
          <FieldLabel path='dataHandling.retentionPolicy'>Retention Policy</FieldLabel>
          {editable ? (
            <Textarea
              className='border-border-divider min-h-[56px] rounded-sm text-[11px]'
              value={(data.retentionPolicy as string) ?? ''}
              onChange={(e) => onChange('retentionPolicy', e.target.value)}
              placeholder='e.g. 7 years'
            />
          ) : (
            <ReadonlyText value={data.retentionPolicy as string} />
          )}
      </div>
      {show('deletionProcess') && (
        <div>
          <FieldLabel path='dataHandling.deletionProcess'>Deletion Process</FieldLabel>
          {editable ? (
            <Textarea
              className='border-border-divider min-h-[56px] rounded-sm text-[11px]'
              value={(data.deletionProcess as string) ?? ''}
              onChange={(e) => onChange('deletionProcess', e.target.value)}
              placeholder='Describe data deletion process'
            />
          ) : (
            <ReadonlyText value={data.deletionProcess as string} />
          )}
        </div>
      )}
      {show('subprocessors') && (
        <div>
          <FieldLabel path='dataHandling.subprocessors'>Subprocessors (comma-separated)</FieldLabel>
          {editable ? (
            <Textarea
              className='border-border-divider min-h-[56px] rounded-sm text-[11px]'
              value={
                Array.isArray(data.subprocessors)
                  ? (data.subprocessors as string[]).join(', ')
                  : ''
              }
              onChange={(e) => {
                const vals = e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
                onChange('subprocessors', vals)
              }}
              placeholder='e.g. Stripe, Twilio'
            />
          ) : (
            <ReadonlyText
              value={
                Array.isArray(data.subprocessors) &&
                (data.subprocessors as string[]).length > 0
                  ? (data.subprocessors as string[]).join(', ')
                  : null
              }
            />
          )}
        </div>
      )}
      <div>
        <FieldLabel path='dataHandling.dataTypes'>Customer data</FieldLabel>
        {editable && onCustomerDataChange ? <VendorCustomerDataSelect
          involved={customerDataInvolved}
          dataTypes={data.dataTypes as string[] | null | undefined}
          onChange={onCustomerDataChange}
        /> : <ReadonlyText value={Array.isArray(data.dataTypes) && data.dataTypes.length
          ? (data.dataTypes as string[]).map(type => ONBOARDING_DATA_TYPE_OPTIONS.find(option => option.value === type)?.label ?? type).join(', ')
          : customerDataInvolved === false ? 'No customer data' : customerDataInvolved === true ? 'Types not specified' : 'Not recorded'} />}
      </div>
      <div>
        <FieldLabel path='dataHandling.processingCountryCodes'>Processing countries</FieldLabel>
        <p className='text-text-subtle mb-2 text-[11px]'>
          Countries where this vendor stores, accesses, or otherwise processes your data, including support access. These can differ from its headquarters. Leave blank if unknown.
        </p>
        {editable ? (
          <MultiSelect
            aria-label='Processing countries'
            options={COUNTRY_OPTIONS}
            value={Array.isArray(data.processingCountryCodes) ? data.processingCountryCodes as string[] : []}
            onValueChange={(values) => onChange('processingCountryCodes', values.length ? values : null)}
            placeholder='Unknown — select countries'
          />
        ) : (
          <ReadonlyText value={Array.isArray(data.processingCountryCodes) && data.processingCountryCodes.length ? (data.processingCountryCodes as string[]).map((code) => COUNTRY_OPTIONS.find((country) => country.value === code)?.label ?? code).join(', ') : null} />
        )}
      </div>
      <div>
        <FieldLabel path='dataHandling.processingLocations'>Processing location details (comma-separated)</FieldLabel>
        <p className='text-text-subtle mb-2 text-[11px]'>
          Optional city, region, or cloud region details, such as Frankfurt or AWS eu-central-1. Select countries above.
        </p>
        {editable ? (
          <Input
            aria-label='Processing locations'
            className='border-border-divider h-8 rounded-sm text-[11px]'
            key={JSON.stringify(data.processingLocations ?? [])}
            defaultValue={Array.isArray(data.processingLocations) ? (data.processingLocations as string[]).join(', ') : ''}
            onBlur={(e) => {
              const previous = Array.isArray(data.processingLocations) ? (data.processingLocations as string[]).join(', ') : ''
              if (e.target.value !== previous) {
                onChange('processingLocations', e.target.value.split(',').map((value) => value.trim()).filter(Boolean))
              }
            }}
          />
        ) : (
          <ReadonlyText value={Array.isArray(data.processingLocations) ? (data.processingLocations as string[]).join(', ') : null} />
        )}
      </div>
      <div>
        <FieldLabel path='dataHandling.notes'>Notes</FieldLabel>
        {editable ? (
          <Textarea
            className='border-border-divider min-h-[80px] rounded-sm text-[11px]'
            value={(data.notes as string) ?? ''}
            onChange={(e) => onChange('notes', e.target.value)}
            placeholder='Data handling notes...'
          />
        ) : (
          <ReadonlyText value={data.notes as string} multiline />
        )}
      </div>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        {[
          ['processingPurpose', 'Processing purpose'],
          ['deletionVerification', 'Deletion verification'],
          ['transferMechanism', 'Transfer mechanism'],
          ['dpaUrl', 'DPA URL'],
          ['minimumNecessaryControls', 'Minimum necessary controls'],
        ].map(([key, label]) => hasFieldValue(data[key]) && (
          <div key={key}><FieldLabel path={`dataHandling.${key}`}>{label}</FieldLabel>{typeof data[key] === 'boolean' ? <ReadonlyBool value={data[key] as boolean} /> : <ReadonlyText value={Array.isArray(data[key]) ? (data[key] as string[]).join(', ') : String(data[key])} multiline />}</div>
        ))}
      </div>
    </div>
  )
}

// ── Compliance Tab ──

function ComplianceTab({
  data,
  editable,
  onChange,
  vendorTier,
  hipaaEnabled,
}: {
  data: Record<string, unknown>
  editable: boolean
  onChange: (key: string, val: unknown) => void
  vendorTier: string | null
  hipaaEnabled: boolean
}) {
  const show = makeFieldVisibility(data, vendorTier, hipaaEnabled)
  const soc2 = (data.soc2 as Record<string, unknown>) ?? {}

  const updateSoc2 = (key: string, val: unknown) => {
    onChange('soc2', { ...soc2, [key]: val })
  }

  return (
    <div className='space-y-4'>
      {/* SOC 2 — status/date/type plus the RFC-231 §3.4 quartet the extractor
          already finds: auditor, period, opinion, exceptions. */}
      <div>
        <FieldLabel>SOC 2</FieldLabel>
        <div className='mt-1 grid grid-cols-3 gap-3'>
          <div>
            <SubLabel path='complianceCerts.soc2.status'>Status</SubLabel>
            {editable ? (
              <Select
                value={(soc2.status as string) ?? '_none'}
                onValueChange={(v) =>
                  updateSoc2('status', v === '_none' ? null : v)
                }
              >
                <SelectTrigger className='border-border-divider h-8 w-full rounded-sm text-[11px]'>
                  <SelectValue placeholder='Not set' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='_none'>Not set</SelectItem>
                  <SelectItem value='Type I'>Type I</SelectItem>
                  <SelectItem value='Type II'>Type II</SelectItem>
                  <SelectItem value='In progress'>In Progress</SelectItem>
                  <SelectItem value='Not applicable'>N/A</SelectItem>
                  {typeof soc2.status === 'string' &&
                    !['Type I', 'Type II', 'In progress', 'Not applicable'].includes(soc2.status) && (
                      <SelectItem value={soc2.status}>{soc2.status} (as extracted)</SelectItem>
                    )}
                </SelectContent>
              </Select>
            ) : (
              <ReadonlyText value={soc2.status as string} />
            )}
          </div>
          <div>
            <SubLabel path='complianceCerts.soc2.reportDate'>Report Date</SubLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(soc2.reportDate as string) ?? ''}
                onChange={(e) => updateSoc2('reportDate', e.target.value)}
                placeholder='YYYY-MM'
              />
            ) : (
              <ReadonlyText value={soc2.reportDate as string} />
            )}
          </div>
          <div>
            <SubLabel path='complianceCerts.soc2.type'>Type</SubLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(soc2.type as string) ?? ''}
                onChange={(e) => updateSoc2('type', e.target.value)}
                placeholder='Type I / II'
              />
            ) : (
              <ReadonlyText value={soc2.type as string} />
            )}
          </div>
          <div>
            <SubLabel path='complianceCerts.soc2.auditor'>Auditor</SubLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(soc2.auditor as string) ?? ''}
                onChange={(e) => updateSoc2('auditor', e.target.value)}
                placeholder='e.g. Prescient Assurance'
              />
            ) : (
              <ReadonlyText value={soc2.auditor as string} />
            )}
          </div>
          <div>
            <SubLabel path='complianceCerts.soc2.auditPeriod'>Audit Period</SubLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(soc2.auditPeriod as string) ?? ''}
                onChange={(e) => updateSoc2('auditPeriod', e.target.value)}
                placeholder='e.g. 2025-01-01 – 2025-12-31'
              />
            ) : (
              <ReadonlyText value={soc2.auditPeriod as string} />
            )}
          </div>
          <div>
            <SubLabel path='complianceCerts.soc2.opinion'>Opinion</SubLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(soc2.opinion as string) ?? ''}
                onChange={(e) => updateSoc2('opinion', e.target.value)}
                placeholder='e.g. Unqualified'
              />
            ) : (
              <ReadonlyText value={soc2.opinion as string} />
            )}
          </div>
          <div>
            <SubLabel path='complianceCerts.soc2.exceptionsNoted'>Exceptions Noted</SubLabel>
            {editable ? (
              <Input
                type='number'
                min={0}
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={typeof soc2.exceptionsNoted === 'number' ? String(soc2.exceptionsNoted) : ''}
                onChange={(e) =>
                  updateSoc2('exceptionsNoted', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder='0'
              />
            ) : (
              <ReadonlyText
                value={typeof soc2.exceptionsNoted === 'number' ? String(soc2.exceptionsNoted) : null}
              />
            )}
          </div>
        </div>
      </div>
      <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
        <div>
          <FieldLabel path='complianceCerts.iso27001'>ISO 27001</FieldLabel>
          {editable ? (
            <BoolSelect
              value={data.iso27001 as boolean | null}
              onChange={(v) => onChange('iso27001', v)}
            />
          ) : (
            <ReadonlyBool value={data.iso27001 as boolean | null} />
          )}
        </div>
        {show('hipaa') && (
          <div>
            <FieldLabel path='complianceCerts.hipaa'>HIPAA</FieldLabel>
            {editable ? (
              <BoolSelect
                value={data.hipaa as boolean | null}
                onChange={(v) => onChange('hipaa', v)}
              />
            ) : (
              <ReadonlyBool value={data.hipaa as boolean | null} />
            )}
          </div>
        )}
        <div>
          <FieldLabel path='complianceCerts.gdpr'>GDPR</FieldLabel>
          {editable ? (
            <BoolSelect
              value={data.gdpr as boolean | null}
              onChange={(v) => onChange('gdpr', v)}
            />
          ) : (
            <ReadonlyBool value={data.gdpr as boolean | null} />
          )}
        </div>
        {show('auditReportsReviewed') && (
          <div>
            <FieldLabel path='complianceCerts.auditReportsReviewed'>Audit Reports Reviewed</FieldLabel>
            {editable ? (
              <BoolSelect
                value={data.auditReportsReviewed as boolean | null}
                onChange={(v) => onChange('auditReportsReviewed', v)}
              />
            ) : (
              <ReadonlyBool
                value={data.auditReportsReviewed as boolean | null}
              />
            )}
          </div>
        )}
      </div>

      {show('otherCerts') && (
        <div>
          <FieldLabel path='complianceCerts.otherCerts'>Other Certifications (comma-separated)</FieldLabel>
          {editable ? (
            <Input
              className='border-border-divider h-8 rounded-sm text-[11px]'
              value={
                Array.isArray(data.otherCerts)
                  ? (data.otherCerts as string[]).join(', ')
                  : ''
              }
              onChange={(e) => {
                const vals = e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
                onChange('otherCerts', vals)
              }}
              placeholder='e.g. PCI DSS, FedRAMP'
            />
          ) : (
            <ReadonlyText
              value={
                Array.isArray(data.otherCerts) &&
                (data.otherCerts as string[]).length > 0
                  ? (data.otherCerts as string[]).join(', ')
                  : null
              }
            />
          )}
        </div>
      )}
      <div>
        <FieldLabel path='complianceCerts.notes'>Notes</FieldLabel>
        {editable ? (
          <Textarea
            className='border-border-divider min-h-[80px] rounded-sm text-[11px]'
            value={(data.notes as string) ?? ''}
            onChange={(e) => onChange('notes', e.target.value)}
            placeholder='Compliance notes...'
          />
        ) : (
          <ReadonlyText value={data.notes as string} multiline />
        )}
      </div>
      {hasFieldValue(soc2.trustPageUrl) && <div><FieldLabel path='complianceCerts.soc2.trustPageUrl'>SOC 2 trust page</FieldLabel><ReadonlyText value={String(soc2.trustPageUrl)} /></div>}
      {hasFieldValue(data.iso27001Details) && <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        {[['certified', 'ISO 27001 certified'], ['certDate', 'Certificate date'], ['registrar', 'Registrar'], ['scope', 'Certification scope'], ['expiresAt', 'Certificate expires']].map(([key, label]) => {
          const value = (data.iso27001Details as Record<string, unknown>)[key]
          return <div key={key}><FieldLabel path={`complianceCerts.iso27001Details.${key}`}>{label}</FieldLabel>{typeof value === 'boolean' ? <ReadonlyBool value={value} /> : <ReadonlyText value={value as string | null} multiline />}</div>
        })}
      </div>}
      {Array.isArray(data.recentBreaches) && data.recentBreaches.length > 0 && <div className='space-y-3'><FieldLabel path='complianceCerts.recentBreaches'>Recent breaches</FieldLabel>{data.recentBreaches.map((breach: { date: string; description: string; recordsAffected?: string; sourceUrl?: string }, index: number) => <div key={index} className='space-y-1 border-l pl-3 text-sm'><p>{breach.date}</p><p className='whitespace-pre-wrap'>{breach.description}</p>{breach.recordsAffected && <p>Records affected: {breach.recordsAffected}</p>}{breach.sourceUrl && <p className='break-all'>{breach.sourceUrl}</p>}</div>)}</div>}
    </div>
  )
}

// ── Contractual Tab ──

function ContractualTab({
  data,
  editable,
  onChange,
  vendorTier,
  hipaaEnabled,
}: {
  data: Record<string, unknown>
  editable: boolean
  onChange: (key: string, val: unknown) => void
  vendorTier: string | null
  hipaaEnabled: boolean
}) {
  const dpaStatusLabels: Record<string, string> = {
    signed: 'Signed', available: 'Available, not signed', not_available: 'Not available',
    not_required: 'Not required', unknown: 'Unknown',
  }
  const show = makeFieldVisibility(data, vendorTier, hipaaEnabled)
  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
        <div>
          <FieldLabel path='contractualReview.slaTerms'>SLA Terms</FieldLabel>
          {editable ? (
            <Input
              className='border-border-divider h-8 rounded-sm text-[11px]'
              value={(data.slaTerms as string) ?? ''}
              onChange={(e) => onChange('slaTerms', e.target.value)}
              placeholder='e.g. 99.9% uptime'
            />
          ) : (
            <ReadonlyText value={data.slaTerms as string} />
          )}
        </div>
        {show('liabilityCap') && (
          <div>
            <FieldLabel path='contractualReview.liabilityCap'>Liability Cap</FieldLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(data.liabilityCap as string) ?? ''}
                onChange={(e) => onChange('liabilityCap', e.target.value)}
                placeholder='e.g. 12 months fees'
              />
            ) : (
              <ReadonlyText value={data.liabilityCap as string} />
            )}
          </div>
        )}
        {show('dataReturnOnTermination') && (
          <div>
            <FieldLabel path='contractualReview.dataReturnOnTermination'>Data Return on Termination</FieldLabel>
            {editable ? (
              <BoolSelect
                value={data.dataReturnOnTermination as boolean | null}
                onChange={(v) => onChange('dataReturnOnTermination', v)}
              />
            ) : (
              <ReadonlyBool
                value={data.dataReturnOnTermination as boolean | null}
              />
            )}
          </div>
        )}
        <div>
          <FieldLabel path='contractualReview.hasCyberInsurance'>Has cyber insurance</FieldLabel>
          {editable ? <BoolSelect label='Has cyber insurance' unknownLabel='Unknown' value={data.hasCyberInsurance as boolean | null} onChange={value => onChange('hasCyberInsurance', value)} /> : <ReadonlyBool value={data.hasCyberInsurance as boolean | null} />}
        </div>
        {(data.hasCyberInsurance === true || show('insuranceCoverage')) && (
          <div>
            <FieldLabel path='contractualReview.insuranceCoverage'>Insurance Coverage</FieldLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(data.insuranceCoverage as string) ?? ''}
                onChange={(e) => onChange('insuranceCoverage', e.target.value)}
                placeholder='e.g. $5M cyber liability'
              />
            ) : (
              <ReadonlyText value={data.insuranceCoverage as string} />
            )}
          </div>
        )}
      </div>
      <div className='grid grid-cols-2 gap-4 lg:grid-cols-3'>
        {show('dpaStatus') && (
          <div>
            <FieldLabel path='contractualReview.dpaStatus'>DPA Status</FieldLabel>
            {editable ? (
              <Select
                value={(data.dpaStatus as string) ?? '_none'}
                onValueChange={(v) => onChange('dpaStatus', v === '_none' ? null : v)}
              >
                <SelectTrigger className='border-border-divider h-8 w-full rounded-sm text-[11px]'>
                  <SelectValue placeholder='Not set' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='_none'>Not set</SelectItem>
                  {Object.entries(dpaStatusLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <ReadonlyText value={dpaStatusLabels[data.dpaStatus as string]} />
            )}
          </div>
        )}
        {show('breachNotificationPeriod') && (
          <div>
            <FieldLabel path='contractualReview.breachNotificationPeriod'>Breach Notification Period</FieldLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(data.breachNotificationPeriod as string) ?? ''}
                onChange={(e) => onChange('breachNotificationPeriod', e.target.value)}
                placeholder='e.g. 72 hours'
              />
            ) : (
              <ReadonlyText value={data.breachNotificationPeriod as string} />
            )}
          </div>
        )}
        {show('transferMechanism') && (
          <div>
            <FieldLabel path='contractualReview.transferMechanism'>Transfer Mechanism</FieldLabel>
            {editable ? (
              <Input
                className='border-border-divider h-8 rounded-sm text-[11px]'
                value={(data.transferMechanism as string) ?? ''}
                onChange={(e) => onChange('transferMechanism', e.target.value)}
                placeholder='e.g. EU SCCs, DPF'
              />
            ) : (
              <ReadonlyText value={data.transferMechanism as string} />
            )}
          </div>
        )}
        {(
          [
            ['auditRightsGranted', 'Audit Rights Granted'],
            ['autoRenewal', 'Auto-Renewal'],
            ['terminationForConvenience', 'Termination for Convenience'],
            ['subprocessorChangeNotification', 'Subprocessor Change Notice'],
          ] as const
        ).map(([key, label]) =>
          show(key) ? (
            <div key={key}>
              <FieldLabel path={`contractualReview.${key}`}>{label}</FieldLabel>
              {editable ? (
                <BoolSelect value={data[key] as boolean | null} onChange={(v) => onChange(key, v)} />
              ) : (
                <ReadonlyBool value={data[key] as boolean | null} />
              )}
            </div>
          ) : null
        )}
      </div>
      <div>
        <FieldLabel path='contractualReview.terminationClause'>Termination Clause</FieldLabel>
        {editable ? (
          <Input
            className='border-border-divider h-8 rounded-sm text-[11px]'
            value={(data.terminationClause as string) ?? ''}
            onChange={(e) => onChange('terminationClause', e.target.value)}
            placeholder='e.g. 30 days notice'
          />
        ) : (
          <ReadonlyText value={data.terminationClause as string} />
        )}
      </div>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        {([
          ['effectiveDate', 'Contract Start Date'],
          ['expirationDate', 'Expiration / Renewal Date'],
        ] as const).map(([key, label]) => {
          const recorded = (data[key] as string) ?? ''
          const parsed = /^\d{4}-\d{2}-\d{2}$/.test(recorded) ? new Date(`${recorded}T00:00:00`) : undefined
          const date = parsed && !Number.isNaN(parsed.getTime()) && parsed.getDate() === Number(recorded.slice(8)) ? parsed : undefined
          return <div key={key}>
            <FieldLabel path={`contractualReview.${key}`} htmlFor={editable ? `vendor-contract-${key}` : undefined}>{label}</FieldLabel>
            {editable ? <>
              <DatePicker
                id={`vendor-contract-${key}`}
                className='border-border-divider h-8 w-full rounded-sm text-[11px]'
                value={date}
                onChange={(value) => onChange(key, value ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}` : null)}
                placeholder={recorded || 'Set date'}
                clearLabel={`Clear ${label}`}
                captionLayout='dropdown'
              />
              {recorded && !date && <Button type='button' variant='ghost' size='sm' onClick={() => onChange(key, null)} aria-label={`Clear ${label}`}>Clear recorded value</Button>}
            </> : <ReadonlyText value={data[key] as string | null} />}
          </div>
        })}
      </div>
      <div>
        <FieldLabel path='contractualReview.notes'>Notes</FieldLabel>
        {editable ? (
          <Textarea
            className='border-border-divider min-h-[80px] rounded-sm text-[11px]'
            value={(data.notes as string) ?? ''}
            onChange={(e) => onChange('notes', e.target.value)}
            placeholder='Contractual notes...'
          />
        ) : (
          <ReadonlyText value={data.notes as string} multiline />
        )}
      </div>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        {[
          ['dpaUrl', 'DPA URL'],
          ['dpaEffectiveDate', 'DPA effective date'],
          ['supportResponseTime', 'Support response time'],
          ['indemnification', 'Indemnification'],
          ['cyberInsuranceMinimum', 'Cyber insurance minimum'],
          ['terminationNotice', 'Termination notice'],
          ['dataReturnPeriod', 'Data return period'],
          ['dataReturnFormat', 'Data return format'],
          ['breachNotificationMethod', 'Breach notification method'],
          ['subprocessorListAvailable', 'Subprocessor list available'],
          ['subprocessorListUrl', 'Subprocessor list URL'],
          ['auditFrequency', 'Audit frequency'],
          ['auditScope', 'Audit scope'],
          ['contractType', 'Contract type'],
          ['governingLaw', 'Governing law'],
          ['riskFlags', 'Risk flags'],
        ].map(([key, label]) => hasFieldValue(data[key]) && (
          <div key={key}><FieldLabel path={`contractualReview.${key}`}>{label}</FieldLabel>{typeof data[key] === 'boolean' ? <ReadonlyBool value={data[key] as boolean} /> : <ReadonlyText value={Array.isArray(data[key]) ? (data[key] as string[]).join(', ') : String(data[key])} multiline />}</div>
        ))}
      </div>
    </div>
  )
}

// ── Controls Tab (persisted control coverage mappings, #370/#469) ──

const FRAMEWORK_CHIP_LABEL: Record<string, string> = {
  hipaa: 'HIPAA',
  'iso-27001': 'ISO 27001',
  'iso-42001': 'ISO 42001',
  gdpr: 'GDPR',
  'soc-2': 'SOC 2',
}

export function ControlsTab({
  mappings,
  isLoading,
  citationInfo,
  documentLinks,
  showAuditState = false,
  onUnlink,
  busy = false,
}: {
  mappings: any[]
  isLoading: boolean
  documentLinks?: Record<string, string>
  onUnlink?: (controlId: string) => void
  busy?: boolean
  showAuditState?: boolean
  citationInfo?: {
    enrolledSlugs: string[]
    soc2Enrolled: boolean
    citations: Record<string, { framework: string; citations: string[] }[]>
  }
}) {
  if (isLoading) {
    return <Skeleton className='h-24 w-full rounded-sm' />
  }
  if (mappings.length === 0) {
    return (
      <div className='py-10 text-center'>
        <p className='font-body text-text-placeholder text-[12px]'>
          No related controls.
        </p>

      </div>
    )
  }

  return (
    <div className='space-y-3 py-1'>
      <p className='font-body text-text-subtle text-[11px]'>{mappings.length} related controls</p>
      <div className='border-border overflow-hidden rounded-sm border'>
        <table className='w-full'>
          <tbody>
            {mappings.map((m) => {
              return (
                <tr key={m.id} className='border-border border-b last:border-0'>
                  <td className='px-3 py-2 align-top'>
                    {/* Framework-neutral diligence criteria: the title leads;
                        every enrolled framework's citation — SOC 2's CC code
                        included — renders as a chip below. */}
                    <span className='font-body text-text-secondary block text-[11px] font-medium'>
                      {m.displayCode && <span className='mr-2 text-cobalt-strong'>{m.displayCode}</span>}{m.controlTitle}
                    </span>
                    {(citationInfo?.citations[m.controlId] ?? []).map((f) => (
                      <span
                        key={f.framework}
                        title={`${FRAMEWORK_CHIP_LABEL[f.framework] ?? f.framework}: ${f.citations.join(', ')}`}
                        className='font-body text-text-subtle bg-surface-subtle border-border-divider mt-1 mr-1 inline-flex max-w-full items-center gap-1 truncate rounded-sm border px-1.5 py-px text-[9.5px]'
                      >
                        <span className='font-medium'>
                          {FRAMEWORK_CHIP_LABEL[f.framework] ?? f.framework}
                        </span>
                        {f.citations.slice(0, 2).join(' · ')}
                        {f.citations.length > 2 && ` +${f.citations.length - 2}`}
                      </span>
                    ))}
                  </td>
                  <td className='font-body text-text-body px-3 py-2 align-top text-[11px]'>
                    {showAuditState && <div className='mb-2 flex items-center gap-2 text-[10px]'>
                      <span>{String(m.coverage).charAt(0) + String(m.coverage).slice(1).toLowerCase().replaceAll('_', ' ')}</span>
                      <span>{m.locked ? 'Locked' : 'Unlocked'}</span>
                    </div>}
                    {m.rationale && m.rationale !== 'Awaiting vendor evidence.' && <p>{m.rationale}</p>}
                    {Array.isArray(m.evidenceCitations) && m.evidenceCitations.length > 0 && <details className='mt-2 space-y-2'><summary className='cursor-pointer'>Evidence citations</summary>{m.evidenceCitations.map((citation: { page: number | null; quote: string }, index: number) => <blockquote key={index} className='whitespace-pre-wrap border-l pl-3'>{citation.page != null && <span>Page {citation.page}: </span>}{citation.quote}</blockquote>)}</details>}
                    {m.documentId && documentLinks?.[m.documentId] && <a className='mt-2 block underline' href={documentLinks[m.documentId]}>View source document</a>}
                  </td>
                  {onUnlink && <td className='px-3 py-2 text-right align-top'><Button variant='ghost' size='smIcon' disabled={busy} aria-label={`Unlink ${m.displayCode ?? m.controlTitle}`} onClick={() => onUnlink(m.controlId)}><X className='size-3.5' /></Button></td>}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

    </div>
  )
}

// ── Summary Tab ──

/** LOW/MEDIUM/HIGH/CRITICAL → Low/Medium/High/Critical. */
function formatTierLabel(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase().replaceAll('_', ' ')
}

function SummaryTab({
  summary,
  riskTier,
  showRating = false,
  editable,
  onSummaryChange,
  assessedRisk,
  onAssessedRiskChange,
}: {
  summary: string
  riskTier: string
  showRating?: boolean
  editable: boolean
  onSummaryChange: (v: string) => void
  assessedRisk?: { impact?: string; likelihood?: string }
  onAssessedRiskChange?: (key: string, value: string) => void
}) {
  return (
    <div className='space-y-4'>
      {(editable || assessedRisk) && <div className='grid gap-4 sm:grid-cols-2'>
        {(['impact', 'likelihood'] as const).map(key => <div key={key}>
          <Label htmlFor={editable ? `assessed-risk-${key}` : undefined} className={cn(DETAIL_FIELD_LABEL_CLASS, 'mb-1.5')}>Assessed {key}</Label>
          {editable ? <Select value={assessedRisk?.[key] ?? ''} onValueChange={value => onAssessedRiskChange?.(key, value)}>
            <SelectTrigger id={`assessed-risk-${key}`} fullWidth><SelectValue placeholder='Select rating' /></SelectTrigger>
            <SelectContent>{RISK_LEVELS.map(level => <SelectItem key={level} value={level}>{RISK_LEVEL_LABELS[level]}</SelectItem>)}</SelectContent>
          </Select> : <ReadonlyText value={assessedRisk?.[key] ? formatTierLabel(assessedRisk[key]!) : null} />}
        </div>)}
      </div>}
      {showRating && <div>
        <FieldLabel>Assessed risk rating</FieldLabel>
        {riskTier ? (
          <span
            className={`font-body inline-flex h-5 items-center rounded-sm border px-2 text-[11px] font-medium ${
              riskTier === 'CRITICAL'
                ? 'border-status-red/30 bg-status-red-bg text-status-red'
                : riskTier === 'HIGH'
                  ? 'border-status-red/30 bg-status-red-bg text-status-red'
                  : riskTier === 'MEDIUM'
                    ? 'border-status-orange/30 bg-status-orange-bg text-status-orange'
                    : 'border-status-green/30 bg-status-green-bg text-status-green'
            }`}
          >
            {riskTier.charAt(0) + riskTier.slice(1).toLowerCase()}
          </span>
        ) : (
          <span className='font-body text-text-placeholder text-[11px] italic'>
            Not set
          </span>
        )}
      </div>}
      <div>
        <FieldLabel required={editable}>Overall Summary</FieldLabel>
        {/* The AI writes the summary as markdown — render/edit it through the
            shared markdown editor instead of showing raw ** markers. */}
        {editable ? (
          <SimpleInstructionsEditor
            value={summary}
            onChange={onSummaryChange}
            placeholder="Summarize the vendor's overall security posture, risk level, and any outstanding concerns..."
            className='min-h-[160px] text-[13px]'
          />
        ) : summary ? (
          <SimpleInstructionsEditor
            value={summary}
            onChange={() => {}}
            readOnly
            className='px-0 text-[13px]'
          />
        ) : (
          <p className='font-body text-text-placeholder text-[12px] italic'>
            No summary yet.
          </p>
        )}
      </div>
      {editable && (!summary || !riskTier) && (
        <p className='font-body text-text-placeholder text-[11px]'>
          Complete the summary and assessed impact and likelihood before submitting for approval.
        </p>
      )}
    </div>
  )
}

// ── Submit for Approval Modal ──

type EligibleReviewer = {
  userId: string
  name: string | null
  email: string | null
  role: string | null
}

/**
 * `canSelfReview` only returns true when the requester is themselves in the
 * eligible set, so the current user is already a normal entry here — the option
 * needs a "self-review" hint, not a synthetic value alongside the real ones.
 */
function toReviewerOptions(
  reviewers: EligibleReviewer[],
  currentUserId: string | undefined,
  allowSelfReview: boolean
): UserSelectMember[] {
  return reviewers
    .filter((r) => r.userId !== currentUserId || allowSelfReview)
    .map((r) => ({
      user: {
        id: r.userId,
        name: r.name,
        email: r.email ?? '',
        image: null,
        role: r.role,
      },
      hint: r.userId === currentUserId ? 'self-review' : undefined,
    }))
}

function SubmitForApprovalModal({
  reviewers,
  allowSelfReview,
  enforceSeparation,
  isLoading,
  currentUserId,
  workspaceId,
  onSubmit,
  onClose,
  isPending,
}: {
  reviewers: EligibleReviewer[]
  allowSelfReview: boolean
  enforceSeparation: boolean
  isLoading: boolean
  currentUserId: string | undefined
  workspaceId: string
  onSubmit: (reviewerId: string) => void
  onClose: () => void
  isPending: boolean
}) {
  const [reviewerId, setReviewerId] = useState('')

  // Whether self-review is offered is the workspace's SoD policy, resolved
  // server-side; the two cases only differ in the explanation we show.
  const reviewerOptions = toReviewerOptions(
    reviewers,
    currentUserId,
    allowSelfReview
  )
  const hasOptions = reviewerOptions.length > 0

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-sm'>
        <DialogHeader>
          <DialogTitle className='font-heading text-[14px]'>
            Submit for Approval
          </DialogTitle>
        </DialogHeader>
        <div className='space-y-4 px-6 py-2'>
          <p className='font-body text-muted-foreground text-[12px]'>
            Choose a reviewer to approve this assessment.
          </p>
          <div>
            <FieldLabel required>Reviewer</FieldLabel>
            {/* Loading / no-reviewers used to be rows inside the dropdown;
                the combobox owns its list, so they read off the trigger. */}
            <UserSelect
              value={reviewerId}
              onValueChange={setReviewerId}
              members={reviewerOptions}
              disabled={isLoading || !hasOptions}
              ariaRequired
              placeholder={
                isLoading
                  ? 'Loading reviewers...'
                  : hasOptions
                    ? 'Select reviewer...'
                    : 'No eligible reviewers'
              }
              className='border-border-divider h-9 rounded-sm text-[12px]'
            />
          </div>

          {!isLoading && allowSelfReview && enforceSeparation && (
            <p className='font-body text-text-placeholder text-[11px]'>
              You're the only admin in this workspace, so you can approve your
              own assessment. The self-review is recorded for audit.{' '}
              <Link
                to='/workspaces/$workspaceId/settings/members'
                params={{ workspaceId }}
                className='text-cobalt-strong hover:underline'
              >
                Invite a reviewer
              </Link>{' '}
              for stronger separation of duties.
            </p>
          )}
          {!isLoading && allowSelfReview && !enforceSeparation && (
            <p className='font-body text-text-placeholder text-[11px]'>
              Separation of preparer and approver isn't enforced in this
              workspace, so you can approve your own assessment. The self-review
              is recorded for audit.{' '}
              <Link
                to='/workspaces/$workspaceId/settings/governance'
                params={{ workspaceId }}
                className='text-cobalt-strong hover:underline'
              >
                Enforce it in governance settings
              </Link>{' '}
              to require a second reviewer.
            </p>
          )}
          {!isLoading && !hasOptions && (
            <p className='font-body text-text-placeholder text-[11px]'>
              Assessments need an admin or reviewer to sign off.{' '}
              <Link
                to='/workspaces/$workspaceId/settings/members'
                params={{ workspaceId }}
                className='text-cobalt-strong hover:underline'
              >
                Invite a reviewer
              </Link>{' '}
              to continue.
            </p>
          )}
        </div>
        <DialogFooter className='flex items-center justify-end gap-2 pt-2'>
          <Button
            variant='outline'
            size='sm'
            className='border-border-divider h-8 rounded-sm text-[12px]'
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size='sm'
            className='bg-primary text-primary-foreground hover:bg-primary/90 h-8 rounded-sm text-[12px]'
            onClick={() => reviewerId && onSubmit(reviewerId)}
            disabled={!reviewerId || isPending}
          >
            {isPending ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Review Modal ──

function ReviewModal({
  action,
  comments,
  onCommentsChange,
  onConfirm,
  onClose,
  isPending,
}: {
  action: 'APPROVED' | 'REJECTED' | 'NEEDS_REVISION'
  comments: string
  onCommentsChange: (v: string) => void
  onConfirm: () => void
  onClose: () => void
  isPending: boolean
}) {
  const actionLabels: Record<string, string> = {
    APPROVED: 'Approve Assessment',
    REJECTED: 'Reject Assessment',
    NEEDS_REVISION: 'Request Revision',
  }
  const buttonStyles: Record<string, string> = {
    APPROVED: 'bg-status-green text-white hover:bg-[#236830]',
    REJECTED: 'bg-status-red text-white hover:bg-[#A93226]',
    NEEDS_REVISION: 'bg-status-orange text-white hover:bg-[#B8720A]',
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className='max-w-sm'>
        <DialogHeader>
          <DialogTitle className='font-heading text-[14px]'>
            {actionLabels[action]}
          </DialogTitle>
        </DialogHeader>
        <div className='space-y-4 px-6 py-2'>
          <div>
            <FieldLabel>
              Comments {action !== 'APPROVED' ? '(required)' : '(optional)'}
            </FieldLabel>
            <Textarea
              className='border-border-divider min-h-[80px] rounded-sm text-[11px]'
              value={comments}
              onChange={(e) => onCommentsChange(e.target.value)}
              placeholder={
                action === 'APPROVED'
                  ? 'Optional approval notes...'
                  : action === 'REJECTED'
                    ? 'Reason for rejection...'
                    : 'What needs to be revised?'
              }
            />
          </div>
        </div>
        <DialogFooter className='flex items-center justify-end gap-2 pt-2'>
          <Button
            variant='outline'
            size='sm'
            className='border-border-divider h-8 rounded-sm text-[12px]'
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size='sm'
            className={`h-8 rounded-sm text-[12px] ${buttonStyles[action]}`}
            onClick={onConfirm}
            disabled={isPending || (action !== 'APPROVED' && !comments)}
          >
            {isPending ? 'Saving...' : actionLabels[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Readonly helpers ──

function ReadonlyBool({ value }: { value: boolean | null | undefined }) {
  return <ReadonlyText value={value == null ? null : value ? 'Yes' : 'No'} />
}

function ReadonlyText({
  value,
  multiline,
}: {
  value: string | null | undefined
  multiline?: boolean
}) {
  const className = cn(DETAIL_FIELD_VALUE_CLASS, 'break-words', !value && 'text-text-placeholder')
  return multiline
    ? <p className={cn(className, 'whitespace-pre-wrap')}>{value || 'Not recorded'}</p>
    : <span className={className}>{value || 'Not recorded'}</span>
}
